import { SETTING_KEYS } from '@/utils/UserSettings.js'
import { DataTableColumn, ColumnSet } from '@/model/DataTableColumn.js'
import { HarvestUnits, TractTypeCategory, TractTypeSpecialization } from '@/utils/Enumerations'
import { formatMoney, numberWithCommas, tonStringFromPounds } from '@/utils/NumericMutations'
import { utcToLocalDate } from '@/utils/DateFormatter'
import i18n from '@/i18n'
import { localeCompareAlphanumeric } from '@/utils/base'
import { sortLandowners } from '@/utils/SortHelpers'
import { AggregateType } from '@/headers/HeaderHelpers'

export default {
  tractHeaders () {
    const columns = [
      new DataTableColumn({
        id: 0,
        sortable: true,
        text: i18n.t('status'),
        value: 'status',
        align: 'center',
        hidable: false
      }),
      new DataTableColumn({
        id: 1,
        sortable: true,
        text: i18n.t('name'),
        value: 'name',
        hidable: false
      }),
      new DataTableColumn({
        id: 38,
        sortable: true,
        text: i18n.t('code'),
        value: 'code',
        group: 'properties'
      }),
      new DataTableColumn({
        id: 2,
        sortable: true,
        text: i18n.t('category'),
        value: 'category',
        hidable: true,
        group: 'properties'
      }),
      new DataTableColumn({
        id: 3,
        sortable: true,
        text: i18n.t('tractType'),
        value: 'typeName',
        hidable: false
      }),
      new DataTableColumn({
        id: 50,
        sortable: true,
        text: i18n.t('group'),
        columnAdjusterText: i18n.t('tractGroup'),
        value: 'tractGroupName',
        hidable: true,
        group: 'properties'
      }),
      new DataTableColumn({
        id: 32,
        sortable: true,
        text: i18n.t('harvestType'),
        value: 'harvestType',
        group: 'properties'
      }),
      new DataTableColumn({
        id: 33,
        sortable: true,
        text: i18n.t('loggingMethod'),
        value: 'loggingMethod',
        group: 'properties'
      }),
      new DataTableColumn({
        id: 34,
        sortable: true,
        text: i18n.t('supplier'),
        value: 'supplierAccount',
        group: 'properties'
      }),
      new DataTableColumn({
        id: 4,
        sortable: true,
        text: i18n.t('landOwner'),
        value: 'landownerBundle',
        group: 'properties',
        sort: sortLandowners
      }),
      new DataTableColumn({
        id: 52,
        sortable: true,
        text: i18n.t('timberCategory'),
        value: 'timberCategory',
        group: 'properties'
      }),
      new DataTableColumn({
        id: 53,
        sortable: true,
        text: i18n.t('conversion'),
        value: 'conversion',
        group: 'properties'
      }),
      new DataTableColumn({
        id: 51,
        sortable: true,
        text: i18n.t('landownerType'),
        value: 'landownerType',
        group: 'properties'
      }),
      new DataTableColumn({
        id: 5,
        sortable: true,
        text: i18n.t('logger'),
        value: 'defaultLoggerName',
        group: 'properties'
      }),
      new DataTableColumn({
        id: 29,
        sortable: true,
        text: i18n.t('team'),
        value: 'teamName',
        group: 'properties'
      }),
      new DataTableColumn({
        id: 6,
        sortable: true,
        text: i18n.t('forester'),
        value: 'foresterUserName',
        group: 'properties'
      }),
      new DataTableColumn({
        id: 19,
        sortable: true,
        text: i18n.t('consultingForester'),
        value: 'consultingForesterAccount',
        group: 'properties'
      }),
      new DataTableColumn({
        id: 7,
        sortable: true,
        text: i18n.t('cost'),
        value: 'cost',
        align: 'right',
        group: 'properties'
      }),
      new DataTableColumn({
        id: 31,
        sortable: true,
        text: i18n.t('costBalance'),
        value: 'costBalance',
        align: 'right',
        group: 'properties',
        tooltip: i18n.t('remainingPurchaseCost')
      }),
      new DataTableColumn({
        id: 30,
        sortable: true,
        text: i18n.t('depletionBalance'),
        value: 'balance',
        align: 'right',
        group: 'properties',
        hidable: true
      }),
      new DataTableColumn({
        id: 25,
        sortable: true,
        text: i18n.t('advanceBalance'),
        value: 'advanceBalance',
        align: 'right',
        hidable: true,
        group: 'properties'
      }),
      new DataTableColumn({
        id: 8,
        sortable: true,
        text: i18n.t('harvestedTons'),
        value: 'harvestedTons',
        align: 'right',
        hidable: true,
        group: 'analysis'
      }),
      new DataTableColumn({
        id: 9,
        sortable: true,
        text: i18n.t('expectedTons'),
        value: 'expectedTons',
        align: 'right',
        hidable: true,
        group: 'analysis'
      }),
      new DataTableColumn({
        id: 36,
        sortable: true,
        text: i18n.t('harvestedMBF'),
        value: 'harvestedMbf',
        align: 'right',
        hidable: true,
        group: 'analysis'
      }),
      new DataTableColumn({
        id: 37,
        sortable: true,
        text: i18n.t('expectedMBF'),
        value: 'expectedMbf',
        align: 'right',
        hidable: true,
        group: 'analysis'
      }),
      new DataTableColumn({
        id: 10,
        sortable: true,
        text: i18n.t('cutoutPercentage'),
        value: 'harvestPercentage',
        align: 'right',
        group: 'analysis',
        sort: localeCompareAlphanumeric
      }),
      new DataTableColumn({
        id: 26,
        sortable: true,
        text: i18n.t('rpi'),
        value: 'rpi',
        align: 'right',
        group: 'properties',
        format: value => value.toFixed(2)
      }),
      new DataTableColumn({
        id: 27,
        sortable: true,
        text: i18n.t('dbh'),
        value: 'dbh',
        align: 'right',
        group: 'properties'
      }),
      new DataTableColumn({
        id: 28,
        sortable: true,
        text: i18n.t('age'),
        value: 'age',
        align: 'right',
        group: 'properties'
      }),
      new DataTableColumn({
        id: 22,
        sortable: true,
        text: i18n.t('loggingAccessibility'),
        value: 'loggingAccessibilityTag',
        group: 'location'
      }),
      new DataTableColumn({
        id: 23,
        sortable: true,
        text: i18n.t('precipitation'),
        value: 'singleDayRainfallInches',
        sort: (a, b) => a !== null ? (a - b) : -9999,
        align: 'right',
        hidable: true,
        group: 'location'
      }),
      new DataTableColumn({
        id: 20,
        sortable: true,
        text: i18n.t('acres'),
        value: 'acres',
        align: 'right',
        group: 'location'
      }),
      new DataTableColumn({
        id: 21,
        sortable: true,
        text: i18n.t('harvestedTonsPerAcre'),
        value: 'harvestedTonsPerAcre',
        align: 'right',
        group: 'analysis'
      }),
      new DataTableColumn({
        id: 11,
        sortable: true,
        text: i18n.t('county'),
        value: 'countrySecondarySubdivision',
        group: 'location'
      }),
      new DataTableColumn({
        id: 12,
        sortable: true,
        text: i18n.t('state'),
        value: 'countrySubdivision',
        group: 'location'
      }),
      new DataTableColumn({
        id: 35,
        sortable: true,
        text: i18n.t('scalingRegion'),
        value: 'scalingRegion',
        group: 'location'
      }),
      new DataTableColumn({
        id: 17,
        sortable: true,
        text: i18n.t('purchaseDate'),
        value: 'purchaseDate',
        align: 'center',
        hidable: true,
        group: 'properties'
      }),
      new DataTableColumn({
        id: 24,
        sortable: true,
        text: i18n.t('bonds'),
        value: 'bondBalance',
        align: 'right',
        hidable: true,
        group: 'properties'
      }),
      new DataTableColumn({
        id: 54,
        sortable: true,
        text: i18n.t('availabilityStart'),
        value: 'availabilityStartDate',
        align: 'center',
        group: 'properties'
      }),
      new DataTableColumn({
        id: 55,
        sortable: true,
        text: i18n.t('availabilityEnd'),
        value: 'availabilityEndDate',
        align: 'center',
        group: 'properties'
      }),
      new DataTableColumn({
        id: 13,
        sortable: true,
        text: i18n.t('harvestStart'),
        value: 'harvestStartDate',
        align: 'center',
        group: 'properties'
      }),
      new DataTableColumn({
        id: 14,
        sortable: true,
        text: i18n.t('harvestEnd'),
        value: 'harvestEndDate',
        align: 'center',
        group: 'properties'
      }),
      new DataTableColumn({
        id: 18,
        sortable: true,
        text: i18n.t('daysRemainingUntilHarvestEnd'),
        value: 'millisecondsTillHarvestEnd',
        align: 'right',
        hidable: true,
        group: 'properties'
      }),
      new DataTableColumn({
        id: 15,
        sortable: false,
        text: i18n.t('related'),
        value: 'related',
        align: 'center',
        hidable: false
      }),
      new DataTableColumn({
        id: 16,
        sortable: false,
        text: i18n.t('actions'),
        value: 'actions',
        align: 'center',
        hidable: false
      })
    ]

    return new ColumnSet({
      columns: columns,
      setName: 'TractGridHeader',
      userSettingKey: SETTING_KEYS.TRACT_GRID_HIDDEN_COLUMNS
    })
  },

  tractGroupHeaders () {
    const columns = [
      new DataTableColumn({
        id: 0,
        sortable: true,
        text: i18n.t('name'),
        value: 'name',
        hidable: false
      }),
      new DataTableColumn({
        id: 1,
        sortable: true,
        text: i18n.t('code'),
        value: 'code',
        group: 'properties'
      }),
      new DataTableColumn({
        id: 3,
        sortable: true,
        text: i18n.t('tractType'),
        value: 'tractTypeName',
        hidable: false
      }),
      new DataTableColumn({
        id: 4,
        sortable: true,
        text: i18n.t('landOwner'),
        value: 'landownerBundle',
        sort: sortLandowners,
        group: 'properties'
      }),
      new DataTableColumn({
        id: 5,
        sortable: true,
        text: i18n.t('team'),
        value: 'teamName',
        group: 'properties'
      }),
      new DataTableColumn({
        id: 6,
        sortable: true,
        text: i18n.t('forester'),
        value: 'foresterUserName',
        group: 'properties'
      }),
      new DataTableColumn({
        id: 7,
        sortable: true,
        text: i18n.t('consultingForester'),
        value: 'consultingForesterName',
        group: 'properties'
      }),
      new DataTableColumn({
        id: 8,
        sortable: true,
        text: i18n.t('businessEntity'),
        value: 'businessEntityName',
        group: 'properties'
      }),
      new DataTableColumn({
        id: 9,
        sortable: true,
        text: i18n.t('cost'),
        value: 'cost',
        align: 'right',
        format: formatMoney,
        group: 'properties'
      }),
      new DataTableColumn({
        id: 12,
        sortable: true,
        text: i18n.t('balance'),
        value: 'balance',
        align: 'right',
        format: formatMoney,
        group: 'properties'
      }),
      new DataTableColumn({
        id: 10,
        sortable: true,
        text: i18n.t('tractCount'),
        value: 'tractCount',
        align: 'right',
        group: 'properties'
      }),
      new DataTableColumn({
        id: 11,
        sortable: false,
        text: i18n.t('actions'),
        value: 'actions',
        align: 'center',
        hidable: false,
        width: '100px'
      })
    ]

    return new ColumnSet({
      columns,
      setName: 'tractGroupHeaders'
    })
  },

  deliveredWoodHeaders () {
    const columns = [
      new DataTableColumn({
        id: 0,
        sortable: true,
        text: i18n.t('name'),
        value: 'tractName',
        hidable: false
      }),
      new DataTableColumn({
        id: 1,
        sortable: true,
        text: i18n.t('supplier'),
        value: 'accountName',
        align: 'left',
        hidable: false
      }),
      new DataTableColumn({
        id: 2,
        sortable: false,
        text: i18n.t('type'),
        value: 'tractType',
        align: 'left',
        hidable: false
      }),
      new DataTableColumn({
        id: 3,
        sortable: true,
        text: i18n.t('rpi'),
        value: 'rpi',
        align: 'right',
        format: value => value.toFixed(2)
      }),
      new DataTableColumn({
        id: 4,
        sortable: true,
        text: i18n.t('dbh'),
        value: 'dbh',
        align: 'right'
      }),
      new DataTableColumn({
        id: 5,
        sortable: true,
        text: i18n.t('age'),
        value: 'age',
        align: 'right'
      }),
      new DataTableColumn({
        id: 6,
        sortable: true,
        text: i18n.t('plf'),
        value: 'plf',
        align: 'right'
      }),
      new DataTableColumn({
        id: 7,
        sortable: true,
        text: i18n.t('sed'),
        value: 'averageSED',
        align: 'right'
      }),
      new DataTableColumn({
        id: 8,
        sortable: true,
        text: i18n.t('loads'),
        value: 'loads',
        align: 'right',
        total: sum
      }),
      new DataTableColumn({
        id: 9,
        sortable: true,
        text: i18n.t('grossTons'),
        value: 'grossTons',
        align: 'right',
        format: (value) => numberWithCommas(value, 3),
        total: sum
      }),
      new DataTableColumn({
        id: 10,
        sortable: true,
        text: i18n.t('netTons'),
        value: 'netTons',
        align: 'right',
        hidable: false,
        format: (value) => numberWithCommas(value, 3),
        total: sum
      }),
      new DataTableColumn({
        id: 11,
        sortable: true,
        text: i18n.t('costPerTon'),
        value: 'costPerTon',
        align: 'right',
        hidable: false,
        format: formatMoney,
        total: ({ items }) => {
          const totalCost = items.reduce((sum, item) => sum + item.totalCost, 0)
          const totalNetTons = items.reduce((sum, item) => sum + item.netTons, 0)
          return totalNetTons > 0 ? totalCost / totalNetTons : 0
        }
      }),
      new DataTableColumn({
        id: 12,
        sortable: false,
        text: i18n.t('totalCost'),
        value: 'totalCost',
        align: 'right',
        format: formatMoney,
        total: sum
      })
    ]
    return new ColumnSet({
      columns: columns,
      setName: 'DeliveredWoodHeaders',
      userSettingKey: SETTING_KEYS.DELIVERED_WOOD_HIDDEN_COLUMNS
    })
  },

  fileHeaders () {
    return [
      {
        sortable: true,
        text: i18n.t('filename'),
        value: 'name',
        align: 'left'
      },
      {
        sortable: true,
        text: i18n.t('type'),
        value: 'type',
        align: 'left'
      },
      {
        sortable: true,
        text: i18n.t('uploadedBy'),
        value: 'user',
        align: 'left'
      },
      {
        sortable: true,
        text: i18n.t('uploadedDate'),
        value: 'date',
        align: 'center'
      },
      {
        sortable: false,
        text: i18n.t('actions'),
        value: 'actions',
        align: 'center',
        hidable: false
      }
    ]
  },

  tractPaymentRegisterHeaders () {
    return [
      {
        sortable: true,
        text: i18n.t('title'),
        value: 'note',
        align: 'left'
      },
      {
        sortable: true,
        text: i18n.t('tract'),
        value: 'tractName'
      },
      {
        sortable: true,
        text: i18n.t('activity'),
        value: 'activity'
      },
      {
        sortable: true,
        text: i18n.t('glCode'),
        value: 'glCode'
      },
      {
        sortable: true,
        text: i18n.t('glOffset'),
        value: 'glOffset'
      },
      {
        sortable: true,
        text: i18n.t('amount'),
        value: 'amount',
        align: 'right'
      }
    ]
  },

  tractPayableHeaders () {
    return [
      {
        sortable: true,
        text: '',
        value: 'type',
        align: 'center'
      },
      {
        sortable: true,
        text: i18n.t('title'),
        value: 'paymentNote'
      },
      {
        sortable: true,
        text: i18n.t('activity'),
        value: 'activityName',
        align: 'center'
      },
      {
        sortable: true,
        text: i18n.t('entity'),
        value: 'businessEntityName',
        align: 'left'
      },
      {
        sortable: true,
        text: i18n.t('account'),
        value: 'accountName',
        align: 'left'
      },
      {
        sortable: true,
        text: i18n.t('issuedAt'),
        value: 'issuedAt',
        align: 'center'
      },
      {
        sortable: true,
        text: i18n.t('payDate'),
        value: 'payDate',
        align: 'center'
      },
      {
        sortable: true,
        text: i18n.t('glCode'),
        value: 'glCode',
        align: 'center'
      },
      {
        sortable: true,
        text: i18n.t('glOffset'),
        value: 'glOffset',
        align: 'center'
      },
      {
        sortable: true,
        text: i18n.t('createdBy'),
        value: 'createdByUser',
        align: 'center'
      },
      {
        sortable: true,
        text: i18n.t('createdAt'),
        value: 'createdAt',
        align: 'center'
      },
      {
        sortable: true,
        text: i18n.t('amount'),
        value: 'amount',
        align: 'right'
      },
      {
        sortable: false,
        text: i18n.t('actions'),
        value: 'actions',
        align: 'center'
      }
    ]
  },

  currentTractPayableHeaders () {
    return [
      {
        sortable: true,
        text: i18n.t('accountName'),
        value: 'accountName',
        align: 'center'
      },
      {
        sortable: true,
        text: i18n.t('tractName'),
        value: 'tractName',
        align: 'center'
      },
      {
        sortable: true,
        text: i18n.t('title'),
        value: 'paymentNote',
        align: 'center'
      },
      {
        sortable: true,
        text: i18n.t('activity'),
        value: 'activityName',
        align: 'center'
      },
      {
        sortable: true,
        text: i18n.t('account'),
        value: 'accountName',
        align: 'left'
      },
      {
        sortable: true,
        text: i18n.t('payDate'),
        value: 'payDate',
        align: 'center'
      },
      {
        sortable: true,
        text: i18n.t('glCode'),
        value: 'glCode',
        align: 'center'
      },
      {
        sortable: true,
        text: i18n.t('createdBy'),
        value: 'createdByUser',
        align: 'center'
      },
      {
        sortable: true,
        text: i18n.t('createdAt'),
        value: 'createdAt',
        align: 'center'
      },
      {
        sortable: true,
        text: i18n.t('amount'),
        value: 'amount',
        align: 'right'
      },
      {
        sortable: false,
        text: i18n.t('actions'),
        value: 'actions',
        align: 'center'
      }
    ]
  },

  depletionBalanceHeaders () {
    const columns = [
      new DataTableColumn({
        id: 0,
        sortable: true,
        text: i18n.t('tractCategory'),
        value: 'tractTypeName',
        align: 'left'
      }),
      new DataTableColumn({
        id: 1,
        sortable: true,
        text: i18n.t('tract'),
        value: 'tractName',
        align: 'left'
      }),
      new DataTableColumn({
        id: 2,
        sortable: true,
        text: i18n.t('cost'),
        value: 'tractCost',
        align: 'right',
        format: formatMoney
      }),
      new DataTableColumn({
        id: 3,
        sortable: true,
        text: i18n.t('depletedInBatch'),
        value: 'depletionInBatch',
        align: 'right',
        format: formatMoney
      }),
      new DataTableColumn({
        id: 4,
        sortable: true,
        text: i18n.t('previouslyDepleted'),
        value: 'previouslyDepleted',
        align: 'right',
        format: formatMoney
      }),
      new DataTableColumn({
        id: 7,
        sortable: true,
        text: i18n.t('totalDepleted'),
        value: 'totalDepleted',
        align: 'right',
        format: formatMoney
      }),
      new DataTableColumn({
        id: 5,
        sortable: true,
        text: i18n.t('purchaseDate'),
        value: 'purchasedAt',
        align: 'right',
        format: (value) => utcToLocalDate(value)
      }),
      new DataTableColumn({
        id: 6,
        sortable: true,
        text: i18n.t('harvestEndDate'),
        value: 'harvestEndDate',
        align: 'right',
        format: (value) => utcToLocalDate(value)
      })
    ]

    return new ColumnSet({
      columns,
      setName: 'depletionHeaders'
    })
  },

  liveDepletionHeaders ({ isPeriod }) {
    const columns = [
      new DataTableColumn({
        id: 0,
        sortable: true,
        text: i18n.t('tractCategory'),
        value: 'tractTypeName',
        align: 'left'
      }),
      new DataTableColumn({
        id: 1,
        sortable: true,
        text: i18n.t('tract'),
        value: 'tractName',
        align: 'left'
      }),
      new DataTableColumn({
        id: 2,
        sortable: true,
        text: i18n.t('cost'),
        value: 'tractCost',
        align: 'right',
        format: formatMoney
      }),
      new DataTableColumn({
        id: 3,
        sortable: true,
        text: i18n.t(isPeriod ? 'depletedInPeriod' : 'depletedInFiscalYear'),
        value: 'depletedInSelection',
        align: 'right',
        format: formatMoney
      }),
      new DataTableColumn({
        id: 4,
        sortable: true,
        text: i18n.t('remaining'),
        value: 'remaining',
        align: 'right',
        format: formatMoney
      }),
      new DataTableColumn({
        id: 5,
        sortable: true,
        text: i18n.t('purchaseDate'),
        value: 'purchasedAt',
        align: 'right',
        format: (value) => utcToLocalDate(value)
      }),
      new DataTableColumn({
        id: 6,
        sortable: true,
        text: i18n.t('harvestEndDate'),
        value: 'harvestEndDate',
        align: 'right',
        format: (value) => utcToLocalDate(value)
      })
    ]

    return new ColumnSet({
      columns,
      setName: 'liveDepletionHeaders'
    })
  },

  liveObligationHeaders ({ isPeriod }) {
    const columns = [
      new DataTableColumn({
        id: 0,
        sortable: true,
        text: i18n.t('tractCategory'),
        value: 'tractTypeName',
        align: 'left'
      }),
      new DataTableColumn({
        id: 1,
        sortable: true,
        text: i18n.t('tractGroup'),
        value: 'tractGroupName',
        align: 'left'
      }),
      new DataTableColumn({
        id: 3,
        sortable: true,
        text: i18n.t(isPeriod ? 'periodTotal' : 'fiscalYearTotal'),
        value: 'amount',
        align: 'right',
        format: formatMoney
      }),
      new DataTableColumn({
        id: 4,
        sortable: true,
        text: i18n.t('remaining'),
        value: 'remaining',
        align: 'right',
        format: formatMoney
      }),
      new DataTableColumn({
        id: 5,
        sortable: true,
        text: i18n.t('tractCount'),
        value: 'tractCount',
        align: 'right'
      }),
      new DataTableColumn({
        id: 6,
        sortable: true,
        text: i18n.t('harvestEndDate'),
        value: 'harvestEndDate',
        align: 'right',
        format: (value) => utcToLocalDate(value)
      })
    ]

    return new ColumnSet({
      columns,
      setName: 'liveObligationHeaders'
    })
  },

  tractProductionHeaders () {
    return [
      {
        sortable: true,
        text: i18n.t('product'),
        value: 'productName'
      },
      {
        sortable: true,
        text: i18n.t('rate'),
        value: 'rate',
        align: 'right'
      },
      {
        sortable: true,
        text: i18n.t('loads'),
        value: 'loads',
        align: 'right'
      },
      {
        sortable: true,
        text: i18n.t('netTons'),
        value: 'weight',
        align: 'right'
      },
      {
        sortable: true,
        text: i18n.t('amount'),
        align: 'right',
        value: 'amount'
      }
    ]
  },

  standingTimberHeaders () {
    const columns = [
      new DataTableColumn({
        id: 16,
        hidable: false,
        sortable: true,
        text: i18n.t('status'),
        value: 'status',
        align: 'center',
        tooltip: i18n.t('status')
      }),
      new DataTableColumn({
        id: 0,
        hidable: false,
        sortable: true,
        text: i18n.t('tract'),
        value: 'tractName',
        align: 'left',
        tooltip: i18n.t('tractName')
      }),
      new DataTableColumn({
        id: 14,
        hidable: true,
        sortable: true,
        text: i18n.t('team'),
        value: 'teamName',
        tooltip: i18n.t('team')
      }),
      new DataTableColumn({
        id: 1,
        hidable: false,
        sortable: true,
        text: i18n.t('forester'),
        value: 'tractForester',
        align: 'left',
        tooltip: i18n.t('forester')
      }),
      new DataTableColumn({
        id: 2,
        hidable: false,
        sortable: true,
        text: i18n.t('type'),
        value: 'tractType',
        align: 'left',
        tooltip: i18n.t('tractType')
      }),
      new DataTableColumn({
        id: 3,
        hidable: true,
        sortable: true,
        text: i18n.t('expectedTons'),
        value: 'expectedTons',
        align: 'right',
        tooltip: i18n.t('cruisedTonsTooltip'),
        format: i => numberWithCommas(i, 3)
      }),
      new DataTableColumn({
        id: 4,
        hidable: true,
        sortable: true,
        text: i18n.t('harvestedTons'),
        value: 'harvestedTons',
        align: 'right',
        tooltip: i18n.t('harvestedTonsTooltip'),
        format: i => numberWithCommas(i, 3)
      }),
      new DataTableColumn({
        id: 5,
        hidable: true,
        sortable: true,
        text: i18n.t('remainingTons'),
        value: 'remainingTons',
        align: 'right',
        tooltip: i18n.t('remainingCruiseTonsTooltip'),
        format: i => numberWithCommas(i, 3)
      }),
      new DataTableColumn({
        id: 6,
        hidable: true,
        sortable: true,
        text: i18n.t('overcutTons'),
        value: 'overcutTons',
        align: 'right',
        tooltip: i18n.t('overcutWeightTooltip'),
        format: i => numberWithCommas(i, 3)
      }),
      new DataTableColumn({
        id: 18,
        hidable: true,
        sortable: true,
        text: i18n.t('expectedMBF'),
        value: 'expectedMBF',
        align: 'right',
        tooltip: i18n.t('cruisedVolumeTooltip'),
        format: i => numberWithCommas(i, 3)
      }),
      new DataTableColumn({
        id: 19,
        hidable: true,
        sortable: true,
        text: i18n.t('harvestedMBF'),
        value: 'harvestedMBF',
        align: 'right',
        tooltip: i18n.t('harvestedVolumeTooltip'),
        format: i => numberWithCommas(i, 3)
      }),
      new DataTableColumn({
        id: 20,
        hidable: true,
        sortable: true,
        text: i18n.t('remainingMBF'),
        value: 'remainingMBF',
        align: 'right',
        tooltip: i18n.t('remainingCruiseVolumeTooltip'),
        format: i => numberWithCommas(i, 3)
      }),
      new DataTableColumn({
        id: 21,
        hidable: true,
        sortable: true,
        text: i18n.t('overcutMBF'),
        value: 'overcutMBF',
        align: 'right',
        tooltip: i18n.t('overcutVolumeTooltip'),
        format: i => numberWithCommas(i, 3)
      }),
      new DataTableColumn({
        id: 7,
        hidable: true,
        sortable: true,
        text: i18n.t('cruisedValue'),
        value: 'cruisedValue',
        align: 'right',
        tooltip: i18n.t('cruiseValueNoHarvestValueTooltip'),
        format: formatMoney
      }),
      new DataTableColumn({
        id: 8,
        hidable: true,
        sortable: true,
        text: i18n.t('cost'),
        value: 'tractCost',
        align: 'right',
        tooltip: i18n.t('costTooltip'),
        format: formatMoney
      }),
      new DataTableColumn({
        id: 17,
        hidable: true,
        sortable: true,
        text: i18n.t('costBalance'),
        value: 'costBalance',
        align: 'right',
        tooltip: i18n.t('remainingPurchaseCost'),
        format: formatMoney
      }),
      new DataTableColumn({
        id: 15,
        hidable: true,
        sortable: true,
        text: i18n.t('depletionBalance'),
        value: 'balance',
        align: 'right',
        tooltip: i18n.t('depletionBalance'),
        format: formatMoney
      }),
      new DataTableColumn({
        id: 9,
        hidable: true,
        sortable: true,
        text: i18n.t('stumpageValue'),
        value: 'stumpageValue',
        align: 'right',
        tooltip: i18n.t('remainingCruiseValueTooltip'),
        format: formatMoney
      }),
      new DataTableColumn({
        id: 10,
        hidable: true,
        sortable: true,
        text: i18n.t('averageCutoutValue'),
        value: 'cutoutValue',
        align: 'right',
        tooltip: i18n.t('averageCutoutValueTooltip'),
        format: formatMoney
      }),
      new DataTableColumn({
        id: 11,
        hidable: true,
        sortable: true,
        text: i18n.t('acres'),
        value: 'tractAcres',
        align: 'right',
        tooltip: i18n.t('tractAcres')
      }),
      new DataTableColumn({
        id: 13,
        hidable: true,
        sortable: true,
        text: i18n.t('loggingAccessibility'),
        value: 'tractLoggingAccessibility',
        align: 'left',
        tooltip: i18n.t('loggingAccessibility')
      }),
      new DataTableColumn({
        id: 12,
        hidable: false,
        sortable: true,
        text: i18n.t('actions'),
        value: 'actions',
        align: 'center',
        tooltip: i18n.t('actions')
      })
    ]
    return new ColumnSet({
      columns,
      setName: 'StandingTimberHeaders',
      userSettingKey: SETTING_KEYS.STANDING_TIMBER_HEADERS
    })
  },

  committedTimberHeaders () {
    const columns = [
      new DataTableColumn({
        id: 13,
        hidable: false,
        sortable: true,
        text: i18n.t('status'),
        value: 'status',
        align: 'center',
        tooltip: i18n.t('status')
      }),
      new DataTableColumn({
        id: 0,
        hidable: false,
        sortable: true,
        text: i18n.t('tract'),
        value: 'tractName',
        align: 'left',
        tooltip: i18n.t('tractName')
      }),
      new DataTableColumn({
        id: 12,
        hidable: true,
        sortable: true,
        text: i18n.t('team'),
        value: 'teamName',
        tooltip: i18n.t('team')
      }),
      new DataTableColumn({
        id: 14,
        hidable: true,
        sortable: true,
        text: i18n.t('supplier'),
        value: 'supplier',
        tooltip: i18n.t('supplier')
      }),
      new DataTableColumn({
        id: 15,
        hidable: true,
        sortable: true,
        text: i18n.t('logger'),
        value: 'tractLogger',
        tooltip: i18n.t('logger')
      }),
      new DataTableColumn({
        id: 1,
        hidable: false,
        sortable: true,
        text: i18n.t('forester'),
        value: 'tractForester',
        align: 'left',
        tooltip: i18n.t('forester')
      }),
      new DataTableColumn({
        id: 2,
        hidable: false,
        sortable: true,
        text: i18n.t('type'),
        value: 'tractType',
        align: 'left',
        tooltip: i18n.t('tractType')
      }),
      new DataTableColumn({
        id: 3,
        hidable: true,
        sortable: true,
        text: i18n.t('expectedTons'),
        value: 'expectedTons',
        align: 'right',
        tooltip: i18n.t('cruisedTonsTooltip'),
        format: i => numberWithCommas(i, 3)
      }),
      new DataTableColumn({
        id: 4,
        hidable: true,
        sortable: true,
        text: i18n.t('harvestedTons'),
        value: 'harvestedTons',
        align: 'right',
        tooltip: i18n.t('harvestedWeightTooltip'),
        format: i => numberWithCommas(i, 3)
      }),
      new DataTableColumn({
        id: 5,
        hidable: true,
        sortable: true,
        text: i18n.t('remainingTons'),
        value: 'remainingTons',
        align: 'right',
        tooltip: i18n.t('remainingCruiseTonsTooltip'),
        format: i => numberWithCommas(i, 3)
      }),
      new DataTableColumn({
        id: 16,
        hidable: true,
        sortable: true,
        text: i18n.t('expectedMBF'),
        value: 'expectedMBF',
        align: 'right',
        tooltip: i18n.t('cruisedVolumeTooltip'),
        format: i => numberWithCommas(i, 3)
      }),
      new DataTableColumn({
        id: 17,
        hidable: true,
        sortable: true,
        text: i18n.t('harvestedMBF'),
        value: 'harvestedMBF',
        align: 'right',
        tooltip: i18n.t('harvestedVolumeTooltip'),
        format: i => numberWithCommas(i, 3)
      }),
      new DataTableColumn({
        id: 18,
        hidable: true,
        sortable: true,
        text: i18n.t('remainingMBF'),
        value: 'remainingMBF',
        align: 'right',
        tooltip: i18n.t('remainingCruiseVolumeTooltip'),
        format: i => numberWithCommas(i, 3)
      }),
      new DataTableColumn({
        id: 6,
        hidable: true,
        sortable: true,
        text: i18n.t('cruisedValue'),
        value: 'cruisedValue',
        align: 'right',
        tooltip: i18n.t('cruiseValueNoHarvestValueTooltip'),
        format: formatMoney
      }),
      new DataTableColumn({
        id: 7,
        sortable: true,
        text: i18n.t('rpi'),
        value: 'rpi',
        align: 'right',
        format: value => value.toFixed(2)
      }),
      new DataTableColumn({
        id: 8,
        sortable: true,
        text: i18n.t('dbh'),
        value: 'dbh',
        align: 'right'
      }),
      new DataTableColumn({
        id: 11,
        sortable: true,
        text: i18n.t('age'),
        value: 'age',
        align: 'right'
      }),
      new DataTableColumn({
        id: 10,
        hidable: true,
        sortable: true,
        text: i18n.t('loggingAccessibility'),
        value: 'tractLoggingAccessibility',
        align: 'left',
        tooltip: i18n.t('loggingAccessibility')
      }),
      new DataTableColumn({
        id: 9,
        hidable: false,
        sortable: true,
        text: i18n.t('actions'),
        value: 'actions',
        align: 'center',
        tooltip: i18n.t('actions')
      })
    ]
    return new ColumnSet({
      columns,
      setName: 'CommittedTimberHeaders',
      userSettingKey: SETTING_KEYS.COMMITTED_TIMBER_HEADERS
    })
  },

  timberPortfolioTractForProductHeaders () {
    const columns = [
      new DataTableColumn({
        id: 0,
        sortable: true,
        text: i18n.t('status'),
        value: 'status',
        align: 'center',
        hidable: false
      }),
      new DataTableColumn({
        id: 1,
        sortable: true,
        text: i18n.t('name'),
        value: 'name',
        hidable: false
      }),
      new DataTableColumn({
        id: 2,
        sortable: true,
        text: i18n.t('type'),
        value: 'typeName',
        hidable: false
      }),
      new DataTableColumn({
        id: 3,
        sortable: true,
        text: i18n.t('logger'),
        value: 'loggerAccountName',
        group: 'properties',
        hidable: false
      }),
      new DataTableColumn({
        id: 4,
        sortable: true,
        text: i18n.t('forester'),
        value: 'foresterUserName',
        group: 'properties',
        hidable: false
      }),
      new DataTableColumn({
        id: 5,
        sortable: true,
        text: i18n.t('tons'),
        value: 'remainingTons',
        align: 'right',
        hidable: false,
        group: 'analysis',
        tooltip: i18n.t('remainingTons')
      }),
      new DataTableColumn({
        id: 11,
        sortable: true,
        text: i18n.t('harvestType'),
        value: 'harvestTypeName',
        align: 'left',
        hidable: true
      }),
      new DataTableColumn({
        id: 12,
        sortable: true,
        text: i18n.t('loggingMethod'),
        value: 'loggingMethodName',
        align: 'left',
        hidable: true
      }),
      new DataTableColumn({
        id: 6,
        sortable: true,
        text: i18n.t('loggingAccessibility'),
        value: 'loggingAccessibilityTagName',
        group: 'location',
        hidable: true
      }),
      new DataTableColumn({
        id: 7,
        sortable: true,
        text: i18n.t('county'),
        value: 'countrySecondarySubdivision',
        group: 'location',
        hidable: true
      }),
      new DataTableColumn({
        id: 8,
        sortable: true,
        text: i18n.t('state'),
        value: 'countrySubdivision',
        group: 'location',
        hidable: true
      }),
      new DataTableColumn({
        id: 9,
        sortable: true,
        text: i18n.t('days'),
        value: 'daysRemaining',
        align: 'right',
        hidable: true,
        group: 'properties',
        tooltip: i18n.t('daysRemainingUntilHarvestEnd')
      }),
      new DataTableColumn({
        id: 10,
        sortable: false,
        text: i18n.t('related'),
        value: 'related',
        align: 'center',
        hidable: false
      })
    ]

    return new ColumnSet({
      columns,
      setName: 'TimberPortfolioHeaders',
      userSettingKey: SETTING_KEYS.TIMBER_PORTFOLIO_HIDDEN_COLUMNS
    })
  },

  timberPortfolioTractForLoggerHeaders () {
    const columns = [
      new DataTableColumn({
        id: 0,
        sortable: true,
        text: i18n.t('status'),
        value: 'status',
        align: 'center',
        hidable: false,
        width: '82px'
      }),
      new DataTableColumn({
        id: 1,
        sortable: true,
        text: i18n.t('tract'),
        value: 'name',
        hidable: false,
        width: '200px'
      }),
      new DataTableColumn({
        id: 2,
        sortable: false,
        text: '',
        value: 'timeline',
        hidable: false
      }),
      new DataTableColumn({
        id: 3,
        sortable: false,
        text: i18n.t('related'),
        value: 'related',
        align: 'center',
        hidable: false,
        width: '92px'
      })
    ]
    return new ColumnSet({
      columns: columns
    })
  },

  tractSummaryHeaders (tractCategory = TractTypeCategory.Delivered.value) {
    const columns = [
      new DataTableColumn({
        id: 0,
        hidable: false,
        sortable: true,
        text: i18n.t('tract'),
        value: 'name'
      }),
      new DataTableColumn({
        id: 1,
        sortable: true,
        hidable: false,
        text: i18n.t('type'),
        value: 'tractType',
        align: 'left'
      }),
      new DataTableColumn({
        id: 20,
        sortable: true,
        text: i18n.t('supplier'),
        value: 'supplier',
        align: 'left'
      }),
      new DataTableColumn({
        id: 21,
        sortable: true,
        text: i18n.t('logger'),
        value: 'logger',
        align: 'left'
      }),
      new DataTableColumn({
        id: 2,
        sortable: true,
        text: i18n.t('loads'),
        value: 'loads',
        align: 'right',
        aggregateType: AggregateType.Sum
      }),
      new DataTableColumn({
        id: 3,
        sortable: true,
        text: i18n.t('plf'),
        value: 'plf',
        align: 'right',
        format: value => value.toFixed(2),
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netWeight'
      }),
      new DataTableColumn({
        id: 4,
        sortable: true,
        text: i18n.t('sed'),
        value: 'averageSED',
        align: 'right',
        format: value => value.toFixed(2),
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netWeight'
      }),
      new DataTableColumn({
        id: 17,
        sortable: true,
        text: i18n.t('rpi'),
        value: 'rpi',
        align: 'right',
        format: value => value.toFixed(2),
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netWeight'
      }),
      new DataTableColumn({
        id: 18,
        sortable: true,
        text: i18n.t('dbh'),
        value: 'dbh',
        align: 'right',
        format: value => Math.round(value),
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netWeight'
      }),
      new DataTableColumn({
        id: 19,
        sortable: true,
        text: i18n.t('age'),
        value: 'age',
        align: 'right',
        format: value => Math.round(value),
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netWeight'
      }),
      new DataTableColumn({
        id: 5,
        sortable: true,
        text: i18n.t('percentageOfTotal'),
        value: 'percentageOfTotal',
        align: 'right',
        format: value => `${(value.toFixed(2))}%`
      }),
      new DataTableColumn({
        id: 6,
        sortable: true,
        text: i18n.t('grossTons'),
        align: 'right',
        value: 'grossWeight',
        format: tonStringFromPounds,
        aggregateType: AggregateType.Sum
      }),
      new DataTableColumn({
        id: 7,
        sortable: true,
        text: i18n.t('netTons'),
        align: 'right',
        value: 'netWeight',
        format: tonStringFromPounds,
        aggregateType: AggregateType.Sum
      }),
      new DataTableColumn({
        id: 8,
        sortable: false,
        text: i18n.t('averageNetTons'),
        align: 'right',
        value: 'averageWeight',
        format: tonStringFromPounds,
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netWeight'
      }),
      new DataTableColumn({
        id: 9,
        sortable: true,
        text: i18n.t('stumpageCostPerTon'),
        align: 'right',
        value: 'stumpageCostPerTon',
        format: value => !value ? '' : formatMoney(value),
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netWeight'
      }),
      new DataTableColumn({
        id: 30,
        sortable: true,
        text: i18n.t('loggingCostPerTon'),
        align: 'right',
        value: 'loggingCostPerTon',
        format: value => !value ? '' : formatMoney(value),
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netWeight'
      }),
      new DataTableColumn({
        id: 31,
        sortable: true,
        text: i18n.t('haulingCostPerTon'),
        align: 'right',
        value: 'haulingCostPerTon',
        format: value => !value ? '' : formatMoney(value),
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netWeight'
      }),
      new DataTableColumn({
        id: 10,
        sortable: true,
        text: i18n.t('productionCostPerTon'),
        align: 'right',
        value: 'productionCostPerTon',
        format: value => !value ? '' : formatMoney(value),
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netWeight'
      }),
      new DataTableColumn({
        id: 11,
        sortable: true,
        text: i18n.t('stumpageCost'),
        align: 'right',
        value: 'stumpageCost',
        format: value => !value ? '' : formatMoney(value),
        aggregateType: AggregateType.Sum
      }),
      new DataTableColumn({
        id: 33,
        sortable: true,
        text: i18n.t('loggingCost'),
        align: 'right',
        value: 'loggingCost',
        aggregateType: AggregateType.Sum,
        format: formatMoney,
        weightedBy: 'netWeight'
      }),
      new DataTableColumn({
        id: 32,
        sortable: true,
        text: i18n.t('haulingCost'),
        align: 'right',
        value: 'haulingCost',
        aggregateType: AggregateType.Sum,
        format: formatMoney,
        weightedBy: 'netWeight'
      }),
      new DataTableColumn({
        id: 12,
        sortable: true,
        text: i18n.t('productionCost'),
        align: 'right',
        value: 'productionCost',
        format: value => !value ? '' : formatMoney(value),
        aggregateType: AggregateType.Sum
      }),
      new DataTableColumn({
        id: 16,
        sortable: true,
        text: i18n.t('costPerGrossTon'),
        align: 'right',
        value: 'costPerGrossTon',
        format: formatMoney,
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netWeight'
      }),
      new DataTableColumn({
        id: 13,
        sortable: true,
        text: i18n.t('costPerNetTon'),
        align: 'right',
        value: 'costPerNetTon',
        format: formatMoney,
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netWeight'
      }),
      new DataTableColumn({
        id: 14,
        sortable: true,
        text: i18n.t('totalCost'),
        align: 'right',
        value: 'totalCost',
        format: formatMoney,
        aggregateType: AggregateType.Sum
      }),
      new DataTableColumn({
        id: 15,
        hidable: false,
        text: i18n.t('actions'),
        align: 'center',
        value: 'actions'
      })
    ]

    const setName = tractCategory === TractTypeCategory.Delivered.value
      ? 'ProductCostDeliveredHiddenColumns'
      : 'ProductCostStumpageHiddenColumns'

    const userSettingKey = tractCategory === TractTypeCategory.Delivered.value
      ? SETTING_KEYS.PRODUCT_COSTS_DELIVERED_HIDDEN_COLUMNS
      : SETTING_KEYS.PRODUCT_COSTS_STUMPAGE_HIDDEN_COLUMNS

    return new ColumnSet({
      columns: columns,
      setName: setName,
      userSettingKey: userSettingKey
    })
  },

  tractSummaryHeadersBySpecies (tractCategory = TractTypeCategory.Delivered.value) {
    const columns = tractCategory === TractTypeCategory.Stumpage.value ? [
      new DataTableColumn({
        id: 0,
        hidable: false,
        sortable: true,
        text: i18n.t('tract'),
        value: 'name'
      }),
      new DataTableColumn({
        id: 1,
        sortable: true,
        hidable: false,
        text: i18n.t('type'),
        value: 'tractType',
        align: 'left'
      }),
      new DataTableColumn({
        id: 2,
        sortable: true,
        text: i18n.t('supplier'),
        value: 'supplier',
        align: 'left'
      }),
      new DataTableColumn({
        id: 3,
        sortable: true,
        text: i18n.t('logger'),
        value: 'logger',
        align: 'left'
      }),
      new DataTableColumn({
        id: 4,
        sortable: true,
        text: i18n.t('rpi'),
        value: 'rpi',
        align: 'right',
        format: value => value.toFixed(2),
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netMbf'
      }),
      new DataTableColumn({
        id: 5,
        sortable: true,
        text: i18n.t('dbh'),
        value: 'dbh',
        align: 'right',
        format: value => Math.round(value),
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netMbf'
      }),
      new DataTableColumn({
        id: 6,
        sortable: true,
        text: i18n.t('age'),
        value: 'age',
        align: 'right',
        format: value => Math.round(value),
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netMbf'
      }),
      new DataTableColumn({
        id: 7,
        sortable: true,
        text: i18n.t('percentageOfTotal'),
        value: 'percentageOfTotal',
        align: 'right',
        format: value => `${(value.toFixed(2))}%`
      }),
      new DataTableColumn({
        id: 8,
        sortable: true,
        text: i18n.t('grossMbf'),
        align: 'right',
        value: 'grossMbf',
        aggregateType: AggregateType.Sum,
        format: (value) => numberWithCommas(value, 3)
      }),
      new DataTableColumn({
        id: 9,
        sortable: true,
        text: i18n.t('netMbf'),
        align: 'right',
        value: 'netMbf',
        aggregateType: AggregateType.Sum,
        format: (value) => numberWithCommas(value, 3)
      }),
      new DataTableColumn({
        id: 10,
        sortable: true,
        text: i18n.t('stumpageCostPerMbf'),
        align: 'right',
        value: 'stumpageCostPerMbf',
        format: value => !value ? '' : formatMoney(value),
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netMbf'
      }),
      new DataTableColumn({
        id: 11,
        sortable: true,
        text: i18n.t('loggingCostPerMbf'),
        align: 'right',
        value: 'loggingCostPerMbf',
        format: value => !value ? '' : formatMoney(value),
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netMbf'
      }),
      new DataTableColumn({
        id: 12,
        sortable: true,
        text: i18n.t('haulingCostPerMbf'),
        align: 'right',
        value: 'haulingCostPerMbf',
        format: value => !value ? '' : formatMoney(value),
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netMbf'
      }),
      new DataTableColumn({
        id: 13,
        sortable: true,
        text: i18n.t('productionCostPerMbf'),
        align: 'right',
        value: 'productionCostPerMbf',
        format: value => !value ? '' : formatMoney(value),
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netMbf'
      }),
      new DataTableColumn({
        id: 14,
        sortable: true,
        text: i18n.t('stumpageCost'),
        align: 'right',
        value: 'stumpageCost',
        format: value => !value ? '' : formatMoney(value),
        aggregateType: AggregateType.Sum
      }),
      new DataTableColumn({
        id: 15,
        sortable: true,
        text: i18n.t('loggingCost'),
        align: 'right',
        value: 'loggingCost',
        aggregateType: AggregateType.Sum,
        format: value => !value ? '' : formatMoney(value),
        weightedBy: 'netMbf'
      }),
      new DataTableColumn({
        id: 16,
        sortable: true,
        text: i18n.t('haulingCost'),
        align: 'right',
        value: 'haulingCost',
        aggregateType: AggregateType.Sum,
        format: value => !value ? '' : formatMoney(value),
        weightedBy: 'netWeight'
      }),
      new DataTableColumn({
        id: 17,
        sortable: true,
        text: i18n.t('productionCost'),
        align: 'right',
        value: 'productionCost',
        format: value => !value ? '' : formatMoney(value),
        aggregateType: AggregateType.Sum
      }),
      new DataTableColumn({
        id: 18,
        sortable: true,
        text: i18n.t('costPerGrossMbf'),
        align: 'right',
        value: 'costPerGrossMbf',
        format: formatMoney,
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netMbf'
      }),
      new DataTableColumn({
        id: 19,
        sortable: true,
        text: i18n.t('costPerNetMbf'),
        align: 'right',
        value: 'costPerNetMbf',
        format: formatMoney,
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netMbf'
      }),
      new DataTableColumn({
        id: 20,
        sortable: true,
        text: i18n.t('totalCost'),
        align: 'right',
        value: 'totalCost',
        format: formatMoney,
        aggregateType: AggregateType.Sum
      }),
      new DataTableColumn({
        id: 21,
        hidable: false,
        text: i18n.t('actions'),
        align: 'center',
        value: 'actions'
      })
    ] : [
      new DataTableColumn({
        id: 0,
        hidable: false,
        sortable: true,
        text: i18n.t('tract'),
        value: 'name'
      }),
      new DataTableColumn({
        id: 1,
        sortable: true,
        hidable: false,
        text: i18n.t('type'),
        value: 'tractType',
        align: 'left'
      }),
      new DataTableColumn({
        id: 2,
        sortable: true,
        text: i18n.t('supplier'),
        value: 'supplier',
        align: 'left'
      }),
      new DataTableColumn({
        id: 3,
        sortable: true,
        text: i18n.t('logger'),
        value: 'logger',
        align: 'left'
      }),
      new DataTableColumn({
        id: 4,
        sortable: true,
        text: i18n.t('rpi'),
        value: 'rpi',
        align: 'right',
        format: value => value.toFixed(2),
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netMbf'
      }),
      new DataTableColumn({
        id: 5,
        sortable: true,
        text: i18n.t('dbh'),
        value: 'dbh',
        align: 'right',
        format: value => Math.round(value),
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netMbf'
      }),
      new DataTableColumn({
        id: 6,
        sortable: true,
        text: i18n.t('age'),
        value: 'age',
        align: 'right',
        format: value => Math.round(value),
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netMbf'
      }),
      new DataTableColumn({
        id: 7,
        sortable: true,
        text: i18n.t('percentageOfTotal'),
        value: 'percentageOfTotal',
        align: 'right',
        format: value => `${(value.toFixed(2))}%`
      }),
      new DataTableColumn({
        id: 8,
        sortable: true,
        text: i18n.t('grossMbf'),
        align: 'right',
        value: 'grossMbf',
        aggregateType: AggregateType.Sum,
        format: (value) => numberWithCommas(value, 3)
      }),
      new DataTableColumn({
        id: 9,
        sortable: true,
        text: i18n.t('netMbf'),
        align: 'right',
        value: 'netMbf',
        aggregateType: AggregateType.Sum,
        format: (value) => numberWithCommas(value, 3)
      }),
      new DataTableColumn({
        id: 10,
        sortable: true,
        text: i18n.t('loggingCostPerMbf'),
        align: 'right',
        value: 'loggingCostPerMbf',
        format: value => !value ? '' : formatMoney(value),
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netMbf'
      }),
      new DataTableColumn({
        id: 11,
        sortable: true,
        text: i18n.t('haulingCostPerMbf'),
        align: 'right',
        value: 'haulingCostPerMbf',
        format: value => !value ? '' : formatMoney(value),
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netMbf'
      }),
      new DataTableColumn({
        id: 12,
        sortable: true,
        text: i18n.t('productionCostPerMbf'),
        align: 'right',
        value: 'productionCostPerMbf',
        format: value => !value ? '' : formatMoney(value),
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netMbf'
      }),
      new DataTableColumn({
        id: 13,
        sortable: true,
        text: i18n.t('loggingCost'),
        align: 'right',
        value: 'loggingCost',
        aggregateType: AggregateType.Sum,
        format: value => !value ? '' : formatMoney(value),
        weightedBy: 'netMbf'
      }),
      new DataTableColumn({
        id: 14,
        sortable: true,
        text: i18n.t('haulingCost'),
        align: 'right',
        value: 'haulingCost',
        aggregateType: AggregateType.Sum,
        format: value => !value ? '' : formatMoney(value),
        weightedBy: 'netWeight'
      }),
      new DataTableColumn({
        id: 15,
        sortable: true,
        text: i18n.t('productionCost'),
        align: 'right',
        value: 'productionCost',
        format: value => !value ? '' : formatMoney(value),
        aggregateType: AggregateType.Sum
      }),
      new DataTableColumn({
        id: 16,
        sortable: true,
        text: i18n.t('costPerGrossMbf'),
        align: 'right',
        value: 'costPerGrossMbf',
        format: formatMoney,
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netMbf'
      }),
      new DataTableColumn({
        id: 17,
        sortable: true,
        text: i18n.t('costPerNetMbf'),
        align: 'right',
        value: 'costPerNetMbf',
        format: formatMoney,
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netMbf'
      }),
      new DataTableColumn({
        id: 18,
        sortable: true,
        text: i18n.t('totalCost'),
        align: 'right',
        value: 'totalCost',
        format: formatMoney,
        aggregateType: AggregateType.Sum
      }),
      new DataTableColumn({
        id: 19,
        hidable: false,
        text: i18n.t('actions'),
        align: 'center',
        value: 'actions'
      })
    ]

    const setName = tractCategory === TractTypeCategory.Delivered.value
      ? 'SpeciesCostDeliveredHiddenColumns'
      : 'SpeciesCostStumpageHiddenColumns'

    const userSettingKey = tractCategory === TractTypeCategory.Delivered.value
      ? SETTING_KEYS.SPECIES_COSTS_DELIVERED_HIDDEN_COLUMNS
      : SETTING_KEYS.SPECIES_COSTS_STUMPAGE_HIDDEN_COLUMNS

    return new ColumnSet({
      columns: columns,
      setName: setName,
      userSettingKey: userSettingKey
    })
  },

  tractCostsDeliveredBySupplierHeaders () {
    const columns = [
      new DataTableColumn({
        id: 0,
        hidable: false,
        sortable: true,
        text: i18n.t('supplier'),
        value: 'supplier'
      }),
      new DataTableColumn({
        id: 1,
        sortable: true,
        text: i18n.t('loads'),
        value: 'loads',
        align: 'right',
        aggregateType: AggregateType.Sum
      }),
      new DataTableColumn({
        id: 2,
        sortable: true,
        text: i18n.t('plf'),
        value: 'plf',
        align: 'right',
        format: value => value.toFixed(2),
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netWeight'
      }),
      new DataTableColumn({
        id: 3,
        sortable: true,
        text: i18n.t('rpi'),
        value: 'rpi',
        align: 'right',
        format: value => value.toFixed(2),
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netWeight'
      }),
      new DataTableColumn({
        id: 4,
        sortable: true,
        text: i18n.t('dbh'),
        value: 'dbh',
        align: 'right',
        format: value => Math.round(value),
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netWeight'
      }),
      new DataTableColumn({
        id: 5,
        sortable: true,
        text: i18n.t('age'),
        value: 'age',
        align: 'right',
        format: value => Math.round(value),
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netWeight'
      }),
      new DataTableColumn({
        id: 6,
        sortable: true,
        text: i18n.t('percentageOfTotal'),
        value: 'percentageOfTotal',
        align: 'right',
        format: value => `${(value.toFixed(2))}%`
      }),
      new DataTableColumn({
        id: 7,
        sortable: true,
        text: i18n.t('grossTons'),
        align: 'right',
        value: 'grossWeight',
        format: tonStringFromPounds,
        aggregateType: AggregateType.Sum
      }),
      new DataTableColumn({
        id: 8,
        sortable: true,
        text: i18n.t('netTons'),
        align: 'right',
        value: 'netWeight',
        format: tonStringFromPounds,
        aggregateType: AggregateType.Sum
      }),
      new DataTableColumn({
        id: 9,
        sortable: true,
        text: i18n.t('totalCost'),
        align: 'right',
        value: 'totalCost',
        format: formatMoney,
        aggregateType: AggregateType.Sum
      }),
      new DataTableColumn({
        id: 10,
        hidable: false,
        text: i18n.t('actions'),
        align: 'center',
        value: 'actions'
      })
    ]

    return new ColumnSet({
      columns: columns,
      setName: 'ProductCostHiddenColumnsBySupplier',
      userSettingKey: SETTING_KEYS.PRODUCT_COSTS_HIDDEN_COLUMNS_BY_SUPPLIER
    })
  },

  tractCostsBySpeciesBySupplierHeaders () {
    const columns = [
      new DataTableColumn({
        id: 0,
        hidable: false,
        sortable: true,
        text: i18n.t('supplier'),
        value: 'supplier'
      }),
      new DataTableColumn({
        id: 1,
        sortable: true,
        text: i18n.t('rpi'),
        value: 'rpi',
        align: 'right',
        format: value => value.toFixed(2),
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netMbf'
      }),
      new DataTableColumn({
        id: 2,
        sortable: true,
        text: i18n.t('dbh'),
        value: 'dbh',
        align: 'right',
        format: value => Math.round(value),
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netMbf'
      }),
      new DataTableColumn({
        id: 3,
        sortable: true,
        text: i18n.t('age'),
        value: 'age',
        align: 'right',
        format: value => Math.round(value),
        aggregateType: AggregateType.WeightedAverage,
        weightedBy: 'netMbf'
      }),
      new DataTableColumn({
        id: 4,
        sortable: true,
        text: i18n.t('percentageOfTotal'),
        value: 'percentageOfTotal',
        align: 'right',
        format: value => `${(value.toFixed(2))}%`
      }),
      new DataTableColumn({
        id: 5,
        sortable: true,
        text: i18n.t('grossMbf'),
        align: 'right',
        value: 'grossMbf',
        aggregateType: AggregateType.Sum,
        format: (value) => numberWithCommas(value, 3)
      }),
      new DataTableColumn({
        id: 6,
        sortable: true,
        text: i18n.t('netMbf'),
        align: 'right',
        value: 'netMbf',
        aggregateType: AggregateType.Sum,
        format: (value) => numberWithCommas(value, 3)
      }),
      new DataTableColumn({
        id: 7,
        sortable: true,
        text: i18n.t('totalCost'),
        align: 'right',
        value: 'totalCost',
        format: formatMoney,
        aggregateType: AggregateType.Sum
      }),
      new DataTableColumn({
        id: 8,
        hidable: false,
        text: i18n.t('actions'),
        align: 'center',
        value: 'actions'
      })
    ]
    return new ColumnSet({
      columns: columns,
      setName: 'SpeciesCostHiddenColumnsBySupplier',
      userSettingKey: SETTING_KEYS.SPECIES_COSTS_HIDDEN_COLUMNS_BY_SUPPLIER
    })
  },

  detailedSummaryHeaders () {
    const columns = [
      new DataTableColumn({
        id: 0,
        hidable: false,
        text: i18n.t('product'),
        value: 'productName'
      }),
      new DataTableColumn({
        id: 1,
        text: i18n.t('loads'),
        value: 'loads',
        align: 'right',
        total: sum
      }),
      new DataTableColumn({
        id: 2,
        text: i18n.t('plf'),
        value: 'plf',
        align: 'right'
      }),
      new DataTableColumn({
        id: 3,
        text: i18n.t('sed'),
        value: 'averageSED',
        align: 'right'
      }),
      new DataTableColumn({
        id: 4,
        text: i18n.t('percentageOfTotal'),
        value: 'percentageOfTotal',
        align: 'right',
        format: value => `${(value.toFixed(2))}%`,
        total: sum
      }),
      new DataTableColumn({
        id: 5,
        text: i18n.t('grossTons'),
        align: 'right',
        value: 'grossWeight',
        format: tonStringFromPounds,
        total: sum
      }),
      new DataTableColumn({
        id: 6,
        hidable: false,
        sortable: false,
        text: i18n.t('netTons'),
        align: 'right',
        value: 'netWeight',
        format: tonStringFromPounds,
        total: sum
      }),
      new DataTableColumn({
        id: 7,
        sortable: false,
        text: i18n.t('averageNetTons'),
        align: 'right',
        value: 'averageWeight',
        format: tonStringFromPounds,
        total: ({ sums }) => sums.netWeight / sums.loads
      }),
      new DataTableColumn({
        id: 8,
        sortable: true,
        text: i18n.t('stumpageCostPerTon'),
        align: 'right',
        value: 'stumpageCostPerTon',
        format: formatMoney,
        total: ({ sums }) => sums.stumpageCost / (sums.netWeight / 2000)
      }),
      new DataTableColumn({
        id: 30,
        sortable: true,
        text: i18n.t('loggingCostPerTon'),
        align: 'right',
        value: 'loggingCostPerTon',
        format: formatMoney,
        total: ({ sums }) => sums.loggingCost / (sums.netWeight / 2000)
      }),
      new DataTableColumn({
        id: 31,
        sortable: true,
        text: i18n.t('haulingCostPerTon'),
        align: 'right',
        value: 'haulingCostPerTon',
        format: formatMoney,
        total: ({ sums }) => sums.haulingCost / (sums.netWeight / 2000)
      }),
      new DataTableColumn({
        id: 9,
        sortable: true,
        text: i18n.t('productionCostPerTon'),
        align: 'right',
        value: 'productionCostPerTon',
        format: formatMoney,
        total: ({ sums }) => sums.productionCost / (sums.netWeight / 2000)
      }),
      new DataTableColumn({
        id: 10,
        sortable: true,
        text: i18n.t('stumpageCost'),
        align: 'right',
        value: 'stumpageCost',
        format: formatMoney,
        total: sum
      }),
      new DataTableColumn({
        id: 33,
        sortable: true,
        text: i18n.t('loggingCost'),
        align: 'right',
        value: 'loggingCost',
        format: formatMoney,
        total: sum
      }),
      new DataTableColumn({
        id: 32,
        sortable: true,
        text: i18n.t('haulingCost'),
        align: 'right',
        value: 'haulingCost',
        format: formatMoney,
        total: sum
      }),
      new DataTableColumn({
        id: 11,
        sortable: true,
        text: i18n.t('productionCost'),
        align: 'right',
        value: 'productionCost',
        format: formatMoney,
        total: sum
      }),
      new DataTableColumn({
        id: 12,
        sortable: false,
        text: i18n.t('costPerTon'),
        align: 'right',
        value: 'costPerTon',
        format: formatMoney,
        total: ({ sums }) => sums.totalCost / (sums.netWeight / 2000)
      }),
      new DataTableColumn({
        id: 13,
        hidable: false,
        sortable: false,
        text: i18n.t('totalCost'),
        align: 'right',
        value: 'totalCost',
        format: formatMoney,
        total: sum
      })
    ]

    return new ColumnSet({
      columns: columns,
      setName: 'TractProductCostHiddenColumns',
      userSettingKey: SETTING_KEYS.TRACT_PRODUCT_COST_HIDDEN_COLUMNS
    })
  },

  detailedSummaryBySpeciesHeaders (tractCategory = TractTypeCategory.Delivered.value) {
    const columns = tractCategory === TractTypeCategory.Stumpage.value ? [
      new DataTableColumn({
        id: 0,
        hidable: false,
        text: i18n.t('species'),
        value: 'speciesName'
      }),
      new DataTableColumn({
        id: 1,
        text: i18n.t('percentageOfTotal'),
        value: 'percentageOfTotal',
        align: 'right',
        format: value => `${(value.toFixed(2))}%`,
        total: sum
      }),
      new DataTableColumn({
        id: 2,
        text: i18n.t('grossMbf'),
        align: 'right',
        value: 'grossMbf',
        format: (value) => numberWithCommas(value, 3),
        total: sum
      }),
      new DataTableColumn({
        id: 3,
        hidable: false,
        sortable: false,
        text: i18n.t('netMbf'),
        align: 'right',
        value: 'netMbf',
        format: (value) => numberWithCommas(value, 3),
        total: sum
      }),
      new DataTableColumn({
        id: 4,
        sortable: true,
        text: i18n.t('stumpageCostPerMbf'),
        align: 'right',
        value: 'stumpageCostPerMbf',
        format: value => !value ? '' : formatMoney(value)
      }),
      new DataTableColumn({
        id: 5,
        sortable: true,
        text: i18n.t('loggingCostPerMbf'),
        align: 'right',
        value: 'loggingCostPerMbf',
        format: value => !value ? '' : formatMoney(value)
      }),
      new DataTableColumn({
        id: 6,
        sortable: true,
        text: i18n.t('haulingCostPerMbf'),
        align: 'right',
        value: 'haulingCostPerMbf',
        format: value => !value ? '' : formatMoney(value)
      }),
      new DataTableColumn({
        id: 7,
        sortable: true,
        text: i18n.t('productionCostPerMbf'),
        align: 'right',
        value: 'productionCostPerMbf',
        format: value => !value ? '' : formatMoney(value)
      }),
      new DataTableColumn({
        id: 8,
        sortable: true,
        text: i18n.t('stumpageCost'),
        align: 'right',
        value: 'stumpageCost',
        format: value => !value ? '' : formatMoney(value),
        total: sum
      }),
      new DataTableColumn({
        id: 9,
        sortable: true,
        text: i18n.t('loggingCost'),
        align: 'right',
        value: 'loggingCost',
        format: value => !value ? '' : formatMoney(value),
        total: sum
      }),
      new DataTableColumn({
        id: 10,
        sortable: true,
        text: i18n.t('haulingCost'),
        align: 'right',
        value: 'haulingCost',
        format: value => !value ? '' : formatMoney(value),
        total: sum
      }),
      new DataTableColumn({
        id: 11,
        sortable: true,
        text: i18n.t('productionCost'),
        align: 'right',
        value: 'productionCost',
        format: value => !value ? '' : formatMoney(value),
        total: sum
      }),
      new DataTableColumn({
        id: 12,
        sortable: false,
        text: i18n.t('costPerGrossMbf'),
        align: 'right',
        value: 'costPerGrossMbf',
        format: value => !value ? '' : formatMoney(value)
      }),
      new DataTableColumn({
        id: 13,
        sortable: false,
        text: i18n.t('costPerNetMbf'),
        align: 'right',
        value: 'costPerNetMbf',
        format: value => !value ? '' : formatMoney(value)
      }),
      new DataTableColumn({
        id: 14,
        hidable: false,
        sortable: false,
        text: i18n.t('totalCost'),
        align: 'right',
        value: 'totalCost',
        format: value => !value ? '' : formatMoney(value),
        total: sum
      })
    ] : [
      new DataTableColumn({
        id: 0,
        hidable: false,
        text: i18n.t('species'),
        value: 'speciesName'
      }),
      new DataTableColumn({
        id: 1,
        text: i18n.t('percentageOfTotal'),
        value: 'percentageOfTotal',
        align: 'right',
        format: value => `${(value.toFixed(2))}%`,
        total: sum
      }),
      new DataTableColumn({
        id: 2,
        text: i18n.t('grossMbf'),
        align: 'right',
        value: 'grossMbf',
        format: (value) => numberWithCommas(value, 3),
        total: sum
      }),
      new DataTableColumn({
        id: 3,
        hidable: false,
        sortable: false,
        text: i18n.t('netMbf'),
        align: 'right',
        value: 'netMbf',
        format: (value) => numberWithCommas(value, 3),
        total: sum
      }),
      new DataTableColumn({
        id: 4,
        sortable: true,
        text: i18n.t('loggingCostPerMbf'),
        align: 'right',
        value: 'loggingCostPerMbf',
        format: value => !value ? '' : formatMoney(value)
      }),
      new DataTableColumn({
        id: 5,
        sortable: true,
        text: i18n.t('haulingCostPerMbf'),
        align: 'right',
        value: 'haulingCostPerMbf',
        format: value => !value ? '' : formatMoney(value)
      }),
      new DataTableColumn({
        id: 6,
        sortable: true,
        text: i18n.t('productionCostPerMbf'),
        align: 'right',
        value: 'productionCostPerMbf',
        format: value => !value ? '' : formatMoney(value)
      }),
      new DataTableColumn({
        id: 7,
        sortable: true,
        text: i18n.t('loggingCost'),
        align: 'right',
        value: 'loggingCost',
        format: value => !value ? '' : formatMoney(value),
        total: sum
      }),
      new DataTableColumn({
        id: 8,
        sortable: true,
        text: i18n.t('haulingCost'),
        align: 'right',
        value: 'haulingCost',
        format: value => !value ? '' : formatMoney(value),
        total: sum
      }),
      new DataTableColumn({
        id: 9,
        sortable: true,
        text: i18n.t('productionCost'),
        align: 'right',
        value: 'productionCost',
        format: value => !value ? '' : formatMoney(value),
        total: sum
      }),
      new DataTableColumn({
        id: 10,
        sortable: false,
        text: i18n.t('costPerGrossMbf'),
        align: 'right',
        value: 'costPerGrossMbf',
        format: value => !value ? '' : formatMoney(value)
      }),
      new DataTableColumn({
        id: 11,
        sortable: false,
        text: i18n.t('costPerNetMbf'),
        align: 'right',
        value: 'costPerNetMbf',
        format: value => !value ? '' : formatMoney(value)
      }),
      new DataTableColumn({
        id: 12,
        hidable: false,
        sortable: false,
        text: i18n.t('totalCost'),
        align: 'right',
        value: 'totalCost',
        format: value => !value ? '' : formatMoney(value),
        total: sum
      })
    ]

    return new ColumnSet({
      columns: columns,
      setName: 'TractSpeciesCostHiddenColumns',
      userSettingKey: SETTING_KEYS.TRACT_SPECIES_COST_HIDDEN_COLUMNS
    })
  },

  detailedTicketHeaders () {
    const columns = [
      new DataTableColumn({
        id: 0,
        hidable: false,
        sortable: false,
        text: i18n.t('ticketNumber'),
        value: 'ticketNumber'
      }),
      new DataTableColumn({
        id: 14,
        hidable: true,
        sortable: false,
        text: i18n.t('extTicketNum1'),
        value: 'extTicketNumber1',
        sort: localeCompareAlphanumeric,
        format: (value) => value === '' ? i18n.t('notAvailable') : value
      }),
      new DataTableColumn({
        id: 15,
        hidable: true,
        sortable: false,
        text: i18n.t('extTicketNum2'),
        value: 'extTicketNumber2',
        sort: localeCompareAlphanumeric,
        format: (value) => value === '' ? i18n.t('notAvailable') : value
      }),
      new DataTableColumn({
        id: 1,
        hidable: false,
        sortable: false,
        text: i18n.t('product'),
        value: 'productName'
      }),
      new DataTableColumn({
        id: 2,
        sortable: false,
        text: i18n.t('plf'),
        value: 'plf',
        align: 'right'
      }),
      new DataTableColumn({
        id: 3,
        sortable: false,
        text: i18n.t('sed'),
        value: 'averageSED',
        align: 'right'
      }),
      new DataTableColumn({
        id: 4,
        sortable: false,
        text: i18n.t('weighedOutAt'),
        value: 'weighedOutAt',
        format: (value) => utcToLocalDate(value, 'MM/DD/YYYY - LT')
      }),
      new DataTableColumn({
        id: 6,
        text: i18n.t('grossTons'),
        align: 'right',
        value: 'grossWeight',
        format: tonStringFromPounds,
        total: sum
      }),
      new DataTableColumn({
        id: 7,
        hidable: false,
        sortable: false,
        text: i18n.t('netTons'),
        align: 'right',
        value: 'netWeight',
        format: tonStringFromPounds,
        total: sum
      }),
      new DataTableColumn({
        id: 8,
        sortable: true,
        text: i18n.t('stumpageCostPerTon'),
        align: 'right',
        value: 'stumpageCostPerTon',
        format: formatMoney,
        total: ({ sums }) => sums.stumpageCost / (sums.netWeight / 2000)
      }),
      new DataTableColumn({
        id: 30,
        sortable: true,
        text: i18n.t('loggingCostPerTon'),
        align: 'right',
        value: 'loggingCostPerTon',
        format: formatMoney,
        total: ({ sums }) => sums.loggingCost / (sums.netWeight / 2000)
      }),
      new DataTableColumn({
        id: 31,
        sortable: true,
        text: i18n.t('haulingCostPerTon'),
        align: 'right',
        value: 'haulingCostPerTon',
        format: formatMoney,
        total: ({ sums }) => sums.haulingCost / (sums.netWeight / 2000)
      }),
      new DataTableColumn({
        id: 9,
        sortable: true,
        text: i18n.t('productionCostPerTon'),
        align: 'right',
        value: 'productionCostPerTon',
        format: formatMoney,
        total: ({ sums }) => sums.productionCost / (sums.netWeight / 2000)
      }),
      new DataTableColumn({
        id: 10,
        sortable: true,
        text: i18n.t('stumpageCost'),
        align: 'right',
        value: 'stumpageCost',
        format: formatMoney,
        total: sum
      }),
      new DataTableColumn({
        id: 33,
        sortable: true,
        text: i18n.t('loggingCost'),
        align: 'right',
        value: 'loggingCost',
        format: formatMoney,
        total: sum
      }),
      new DataTableColumn({
        id: 32,
        sortable: true,
        text: i18n.t('haulingCost'),
        align: 'right',
        value: 'haulingCost',
        format: formatMoney,
        total: sum
      }),
      new DataTableColumn({
        id: 11,
        sortable: true,
        text: i18n.t('productionCost'),
        align: 'right',
        value: 'productionCost',
        format: formatMoney,
        total: sum
      }),
      new DataTableColumn({
        id: 12,
        sortable: false,
        text: i18n.t('costPerTon'),
        align: 'right',
        value: 'costPerTon',
        format: formatMoney,
        total: ({ sums }) => sums.totalCost / (sums.netWeight / 2000)
      }),
      new DataTableColumn({
        id: 13,
        sortable: false,
        hidable: false,
        text: i18n.t('totalCost'),
        align: 'right',
        value: 'totalCost',
        format: formatMoney,
        total: sum
      })
    ]

    return new ColumnSet({
      columns: columns,
      setName: 'TicketProductCostHiddenColumns',
      userSettingKey: SETTING_KEYS.TICKET_PRODUCT_COSTS_HIDDEN_COLUMNS
    })
  },

  templateBundleHeaders () {
    return [
      {
        sortable: true,
        text: '',
        value: 'activityStatus'
      },
      {
        sortable: true,
        text: i18n.t('name'),
        value: 'activityTemplateName',
        align: 'left'
      },
      {
        sortable: true,
        text: i18n.t('counterparty'),
        value: 'counterpartyName'
      },
      {
        sortable: true,
        text: i18n.t('entity'),
        value: 'businessEntityName'
      },
      {
        sortable: false,
        text: i18n.t('recoveryMode'),
        value: 'recovers'
      },
      {
        sortable: true,
        text: i18n.t('baseRate'),
        value: 'baseRate',
        width: '150',
        align: 'right'
      },
      {
        sortable: false,
        text: i18n.t('rateSheet'),
        value: 'rateSheet',
        width: '150'
      },
      {
        sortable: true,
        text: i18n.t('payOn'),
        value: 'payOn'
      },
      {
        sortable: true,
        text: i18n.t('payBy'),
        value: 'payBy'
      },
      {
        sortable: true,
        text: i18n.t('modifier'),
        value: 'modifier',
        align: 'center'
      },
      {
        sortable: true,
        text: i18n.t('actions'),
        value: 'actions',
        align: 'center'
      }
    ]
  },

  bundleProductHeaders () {
    return ([
      {
        sortable: true,
        text: i18n.t('product'),
        value: 'productName'
      },
      {
        sortable: true,
        text: i18n.t('rate'),
        value: 'baseRate',
        width: '250',
        align: 'right'
      }
    ])
  }
}

const formatWithCommas = v => numberWithCommas(v, 3)
const formatPct = p => `${Math.round(p)}%`
const sum = ({ total }) => total

export const tractHarvestHeaders = (tractTypeCategory, harvestUnits = HarvestUnits.Tons, specialization = null, isUnit = false) => {
  const isStumpage = tractTypeCategory === TractTypeCategory.Stumpage
  const isTons = harvestUnits === HarvestUnits.Tons
  const isObligationUncapped = specialization === TractTypeSpecialization.ObligationUncapped.value
  const isObligation = isObligationUncapped ||
    specialization === TractTypeSpecialization.ObligationCapped.value

  if (isUnit) {
    const unitColumns = [
      new DataTableColumn({
        id: 0,
        text: isTons ? i18n.t('product') : i18n.t('species'),
        value: isTons ? 'product' : 'species',
        tooltip: isTons ? i18n.t('productTooltip') : i18n.t('speciesTooltip'),
        hidable: false
      }),
      new DataTableColumn({
        id: 2,
        text: isTons ? i18n.t('cruisePricePerTon') : i18n.t('cruisePricePerMBF'),
        value: isTons ? 'pricePerTon' : 'pricePerMbf',
        align: 'right',
        tooltip: isTons ? i18n.t('cruisePricePerTonTooltip') : i18n.t('cruisePricePerMBFTooltip'),
        format: formatMoney,
        group: 'cruise'
      }),
      new DataTableColumn({
        id: 3,
        text: isTons ? i18n.t('cruisedTons') : i18n.t('cruisedMBF'),
        value: isTons ? 'expectedTons' : 'expectedMbf',
        align: 'right',
        tooltip: isTons ? i18n.t('cruisedTonsTooltip') : i18n.t('cruisedMBFTooltip'),
        format: formatWithCommas,
        total: sum,
        group: 'cruise'
      }),
      new DataTableColumn({
        id: 5,
        text: isTons ? i18n.t('harvestedTons') : i18n.t('harvestedMBF'),
        textAbbr: isTons ? i18n.t('harvestedTonsAbbr') : i18n.t('harvestedMBFAbbr'),
        value: isTons ? 'harvestedTons' : 'harvestedMbf',
        align: 'right',
        tooltip: isTons ? i18n.t('harvestedTonsTooltip') : i18n.t('harvestedMBFTooltip'),
        format: formatWithCommas,
        total: sum,
        group: 'operational'
      }),
      new DataTableColumn({
        id: 6,
        text: i18n.t('harvested'),
        textAbbr: i18n.t('harvestedAbbr'),
        value: 'percentageOfTotalHarvest',
        align: 'right',
        tooltip: isTons ? i18n.t('harvestedTonsPercentageTooltip') : i18n.t('harvestedMBFPercentageTooltip'),
        format: formatPct,
        total: sum,
        group: 'operational',
        columnAdjusterText: i18n.t('harvestedPercentage')
      }),
      new DataTableColumn({
        id: 8,
        text: i18n.t('cutout'),
        value: 'cutoutPercentage',
        align: 'right',
        tooltip: i18n.t('cutoutPercentageTooltip'),
        format: formatPct,
        group: 'operational',
        columnAdjusterText: i18n.t('cutoutPercentage')
      }),
      !isTons && isObligationUncapped ? new DataTableColumn({
        id: 25,
        text: i18n.t('obligationPerMBF'),
        value: 'obligationRate',
        align: 'right',
        tooltip: i18n.t('obligationPerMBFTooltip'),
        format: formatMoney,
        group: 'cruise'
      }) : undefined,
      !isTons && isObligationUncapped ? new DataTableColumn({
        id: 26,
        text: i18n.t('totalObligation'),
        value: 'totalObligation',
        align: 'right',
        tooltip: i18n.t('totalObligationTooltip'),
        format: formatMoney,
        total: sum,
        group: 'cruise'
      }) : undefined,
      !isTons && isObligation ? new DataTableColumn({
        id: 28,
        text: i18n.t('obligationAccrued'),
        value: 'obligationAccrued',
        align: 'right',
        tooltip: i18n.t('obligationAccruedTooltip'),
        format: formatMoney,
        total: sum,
        group: 'cruise'
      }) : undefined,
      !isTons && isObligationUncapped ? new DataTableColumn({
        id: 27,
        text: i18n.t('obligationBalance'),
        value: 'obligationBalance',
        align: 'right',
        tooltip: i18n.t('obligationBalanceTooltip'),
        format: formatMoney,
        total: sum,
        group: 'cruise'
      }) : undefined,
      isTons || isStumpage ? new DataTableColumn({
        id: 15,
        text: i18n.t('stumpage'),
        value: 'totalStumpageCost',
        align: 'right',
        tooltip: i18n.t('stumpageCostCruiseTooltip'),
        format: formatMoney,
        total: sum,
        group: 'financial',
        columnAdjusterText: i18n.t('stumpageCostCruise')
      }) : undefined,
      new DataTableColumn({
        id: 14,
        text: i18n.t('production'),
        textAbbr: i18n.t('productionAbbr'),
        value: 'totalProductionCost',
        align: 'right',
        tooltip: i18n.t('totalProductionCostCruiseTooltip'),
        format: formatMoney,
        total: sum,
        group: 'financial',
        columnAdjusterText: i18n.t('totalProductionCostCruise')
      })
    ].filter(dt => dt !== undefined)

    return new ColumnSet({
      columns: unitColumns,
      setName: 'TractHarvestHeaders',
      userSettingKey: SETTING_KEYS.TRACT_CRUISE_UNIT_HIDDEN_COLUMNS
    })
  }

  const columns = [
    new DataTableColumn({
      id: 0,
      text: isTons ? i18n.t('product') : i18n.t('species'),
      value: isTons ? 'product' : 'species',
      tooltip: isTons ? i18n.t('productTooltip') : i18n.t('speciesTooltip'),
      hidable: false
    }),
    new DataTableColumn({
      id: 2,
      text: isTons ? i18n.t('cruisePricePerTon') : i18n.t('cruisePricePerMBF'),
      value: isTons ? 'pricePerTon' : 'pricePerMbf',
      align: 'right',
      tooltip: isTons ? i18n.t('cruisePricePerTonTooltip') : i18n.t('cruisePricePerMBFTooltip'),
      hidable: false,
      format: formatMoney,
      group: 'cruise'
    }),
    new DataTableColumn({
      id: 3,
      text: isTons ? i18n.t('cruisedTons') : i18n.t('cruisedMBF'),
      value: isTons ? 'expectedTons' : 'expectedMbf',
      align: 'right',
      tooltip: isTons ? i18n.t('cruisedTonsTooltip') : i18n.t('cruisedMBFTooltip'),
      hidable: false,
      format: formatWithCommas,
      total: sum,
      group: 'cruise'
    }),
    new DataTableColumn({
      id: 4,
      text: i18n.t('cruisedValue'),
      value: 'cruisedValue',
      align: 'right',
      tooltip: i18n.t('cruisedValueTooltip'),
      hidable: false,
      format: formatMoney,
      total: sum,
      group: 'cruise'
    }),
    !isTons && isObligationUncapped ? new DataTableColumn({
      id: 25,
      text: i18n.t('obligationPerMBF'),
      value: 'obligationRate',
      align: 'right',
      tooltip: i18n.t('obligationPerMBFTooltip'),
      format: formatMoney,
      group: 'cruise'
    }) : undefined,
    !isTons && isObligationUncapped ? new DataTableColumn({
      id: 26,
      text: i18n.t('totalObligation'),
      value: 'totalObligation',
      align: 'right',
      tooltip: i18n.t('totalObligationTooltip'),
      format: formatMoney,
      total: sum,
      group: 'cruise'
    }) : undefined,
    !isTons && isObligation ? new DataTableColumn({
      id: 28,
      text: i18n.t('obligationAccrued'),
      value: 'obligationAccrued',
      align: 'right',
      tooltip: i18n.t('obligationAccruedTooltip'),
      format: formatMoney,
      total: sum,
      group: 'cruise'
    }) : undefined,
    !isTons && isObligationUncapped ? new DataTableColumn({
      id: 27,
      text: i18n.t('obligationBalance'),
      value: 'obligationBalance',
      align: 'right',
      tooltip: i18n.t('obligationBalanceTooltip'),
      format: formatMoney,
      total: sum,
      group: 'cruise'
    }) : undefined,
    isStumpage && isTons ? new DataTableColumn({
      id: 17,
      text: i18n.t('purchaseAllocation'),
      textAbbr: i18n.t('purchaseAllocationAbbr'),
      value: 'purchaseAllocation',
      align: 'right',
      tooltip: i18n.t('purchaseAllocationCruiseTooltip'),
      format: formatMoney,
      total: sum,
      group: 'financial'
    }) : undefined,
    isTons ? new DataTableColumn({
      id: 1,
      text: i18n.t('loads'),
      value: 'loads',
      align: 'right',
      hidable: false,
      tooltip: i18n.t('loadsTooltip'),
      total: sum
    }) : undefined,
    new DataTableColumn({
      id: 5,
      text: isTons ? i18n.t('harvestedTons') : i18n.t('harvestedMBF'),
      textAbbr: isTons ? i18n.t('harvestedTonsAbbr') : i18n.t('harvestedMBFAbbr'),
      value: isTons ? 'harvestedTons' : 'harvestedMbf',
      align: 'right',
      tooltip: isTons ? i18n.t('harvestedTonsTooltip') : i18n.t('harvestedMBFTooltip'),
      format: formatWithCommas,
      total: sum,
      group: 'operational'
    }),
    new DataTableColumn({
      id: 6,
      text: i18n.t('harvested'),
      textAbbr: i18n.t('harvestedAbbr'),
      value: 'percentageOfTotalHarvest',
      align: 'right',
      tooltip: isTons ? i18n.t('harvestedTonsPercentageTooltip') : i18n.t('harvestedMBFPercentageTooltip'),
      format: formatPct,
      total: sum,
      group: 'operational',
      columnAdjusterText: i18n.t('harvestedPercentage')
    }),
    new DataTableColumn({
      id: 7,
      text: isTons ? i18n.t('remainingCruiseTons') : i18n.t('remainingCruiseMBF'),
      textAbbr: isTons ? i18n.t('remainingCruiseTonsAbbr') : i18n.t('remainingCruiseMBFAbbr'),
      value: isTons ? 'remainingTons' : 'remainingMbf',
      align: 'right',
      tooltip: isTons ? i18n.t('remainingTonsTooltip') : i18n.t('remainingMBFTooltip'),
      format: formatWithCommas,
      total: sum,
      group: 'operational'
    }),
    new DataTableColumn({
      id: 8,
      text: i18n.t('cutout'),
      value: 'cutoutPercentage',
      align: 'right',
      tooltip: i18n.t('cutoutPercentageTooltip'),
      format: formatPct,
      group: 'operational',
      columnAdjusterText: i18n.t('cutoutPercentage')
    }),
    new DataTableColumn({
      id: 9,
      text: isTons ? i18n.t('overcutTons') : i18n.t('overcutMBF'),
      value: isTons ? 'overcutTons' : 'overcutMbf',
      align: 'right',
      tooltip: isTons ? i18n.t('overcutTonsTooltip') : i18n.t('overcutMBFTooltip'),
      format: formatWithCommas,
      total: sum,
      group: 'operational'
    }),
    new DataTableColumn({
      id: 10,
      text: i18n.t('overcut'),
      value: 'overcutPercentage',
      align: 'right',
      tooltip: i18n.t('overcutPercentageTooltip'),
      format: formatPct,
      group: 'operational',
      columnAdjusterText: i18n.t('overcutPercentage')
    }),
    new DataTableColumn({
      id: 18,
      text: i18n.t('harvestedCruise'),
      textAbbr: i18n.t('harvestedCruiseAbbr'),
      value: 'harvestedCruiseValue',
      align: 'right',
      tooltip: i18n.t('harvestedCruiseValueTooltip'),
      format: formatMoney,
      total: sum,
      group: 'financial',
      columnAdjusterText: i18n.t('harvestedCruiseValue')
    }),
    new DataTableColumn({
      id: 19,
      text: i18n.t('overUnderCruise'),
      value: 'overUnderCruiseValue',
      align: 'right',
      tooltip: i18n.t('overUnderCruiseValueTooltip'),
      format: formatMoney,
      total: sum,
      group: 'financial'
    }),
    new DataTableColumn({
      id: 11,
      text: i18n.t('remainingCruise'),
      textAbbr: i18n.t('remainingCruiseValueAbbr'),
      value: 'remainingCruiseValue',
      align: 'right',
      tooltip: i18n.t('remainingCruiseValueTooltip'),
      format: formatMoney,
      total: sum,
      group: 'financial',
      columnAdjusterText: i18n.t('remainingCruiseValue')
    }),
    // Start depletion-status-dependent section
    isTons || isStumpage ? new DataTableColumn({
      id: 15,
      text: i18n.t('stumpage'),
      value: 'totalStumpageCost',
      align: 'right',
      tooltip: i18n.t('stumpageCostCruiseTooltip'),
      format: formatMoney,
      total: sum,
      group: 'financial',
      columnAdjusterText: i18n.t('stumpageCostCruise')
    }) : undefined,
    isStumpage && isTons && !isUnit ? new DataTableColumn({
      id: 20,
      text: i18n.t('overUnderPurchase'),
      textAbbr: i18n.t('overUnderPurchaseAbbr'),
      value: 'overUnderPurchaseValue',
      align: 'right',
      tooltip: i18n.t('overUnderPurchaseValueTooltip'),
      format: formatMoney,
      total: sum,
      group: 'financial',
      columnAdjusterText: i18n.t('overUnderPurchaseValue')
    }) : undefined,
    isStumpage && isTons && !isUnit ? new DataTableColumn({
      id: 21,
      text: i18n.t('remainingPurchase'),
      textAbbr: i18n.t('remainingPurchaseAbbr'),
      value: 'remainingPurchaseValue',
      align: 'right',
      tooltip: i18n.t('remainingPurchaseValueTooltip'),
      format: formatMoney,
      group: 'financial',
      columnAdjusterText: i18n.t('remainingPurchaseValue')
    }) : undefined,
    // End depletion-status-dependent section
    new DataTableColumn({
      id: 14,
      text: i18n.t('production'),
      textAbbr: i18n.t('productionAbbr'),
      value: 'totalProductionCost',
      align: 'right',
      tooltip: i18n.t('totalProductionCostCruiseTooltip'),
      format: formatMoney,
      total: sum,
      group: 'financial',
      columnAdjusterText: i18n.t('totalProductionCostCruise')
    }),
    new DataTableColumn({
      id: 16,
      text: i18n.t('total'),
      value: 'totalCost',
      align: 'right',
      tooltip: i18n.t('totalCruiseCostTooltip'),
      hidable: false,
      format: formatMoney,
      total: sum
    }),
    isStumpage ? new DataTableColumn({
      id: 22,
      text: i18n.t('revenue'),
      value: 'revenue',
      align: 'right',
      tooltip: i18n.t('revenueCruiseTooltip'),
      format: formatMoney,
      total: sum,
      group: 'financial'
    }) : undefined,
    isStumpage ? new DataTableColumn({
      id: 23,
      text: i18n.t('grossProfit'),
      value: 'grossProfit',
      align: 'right',
      tooltip: i18n.t('grossProfitCruiseTooltip'),
      format: formatMoney,
      total: sum,
      group: 'financial'
    }) : undefined,
    isStumpage ? new DataTableColumn({
      id: 24,
      text: isTons ? i18n.t('grossProfitPerTon') : i18n.t('grossProfitPerMBF'),
      value: isTons ? 'grossProfitPerTon' : 'grossProfitPerMbf',
      align: 'right',
      tooltip: isTons ? i18n.t('grossProfitPerTonCruiseTooltip') : i18n.t('grossProfitPerMBFCruiseTooltip'),
      format: formatMoney,
      group: 'financial',
      total: ({ sums }) => sums.grossProfit / (isTons ? sums.harvestedTons : sums.harvestedMbf)
    }) : undefined
  ].filter(dt => dt !== undefined)

  return new ColumnSet({
    columns: columns,
    setName: 'TractHarvestHeaders',
    userSettingKey: SETTING_KEYS.TRACT_CRUISE_HIDDEN_COLUMNS
  })
}

export const tractDepletionHarvestHeaders = (tractTypeCategory, harvestUnits = HarvestUnits.Tons, isUnit = false) => {
  const isStumpage = tractTypeCategory === TractTypeCategory.Stumpage
  const isTons = harvestUnits === HarvestUnits.Tons

  const columns = [
    new DataTableColumn({
      id: 0,
      text: i18n.t('product'),
      value: 'product',
      tooltip: i18n.t('productTooltip'),
      hidable: false
    }),
    new DataTableColumn({
      id: 2,
      text: isTons ? i18n.t('cruisePricePerTon') : i18n.t('cruisePricePerMBF'),
      value: isTons ? 'pricePerTon' : 'pricePerMBF',
      align: 'right',
      tooltip: i18n.t('cruisePricePerTonTooltip'),
      hidable: false,
      format: formatMoney,
      group: 'cruise'
    }),
    new DataTableColumn({
      id: 3,
      text: isTons ? i18n.t('cruisedTons') : i18n.t('cruisedMBF'),
      value: isTons ? 'expectedTons' : 'expectedMBF',
      align: 'right',
      tooltip: i18n.t('cruisedTonsTooltip'),
      hidable: false,
      format: formatWithCommas,
      total: sum,
      group: 'cruise'
    }),
    new DataTableColumn({
      id: 4,
      text: i18n.t('cruisedValue'),
      value: 'cruisedValue',
      align: 'right',
      tooltip: i18n.t('cruisedValueTooltip'),
      hidable: false,
      format: formatMoney,
      total: sum,
      group: 'cruise'
    }),
    isStumpage ? new DataTableColumn({
      id: 17,
      text: i18n.t('purchaseAllocation'),
      textAbbr: i18n.t('purchaseAllocationAbbr'),
      value: 'purchaseAllocation',
      align: 'right',
      tooltip: i18n.t('purchaseAllocationCruiseTooltip'),
      format: formatMoney,
      total: sum,
      group: 'financial'
    }) : undefined,
    new DataTableColumn({
      id: 1,
      text: i18n.t('loads'),
      value: 'loads',
      align: 'right',
      hidable: false,
      tooltip: i18n.t('loadsTooltip'),
      total: sum
    }),
    new DataTableColumn({
      id: 5,
      text: isTons ? i18n.t('harvestedTons') : i18n.t('harvestedMBF'),
      textAbbr: isTons ? i18n.t('harvestedTonsAbbr') : i18n.t('harvestedMBFAbbr'),
      value: isTons ? 'harvestedTons' : 'harvestedMBF',
      align: 'right',
      tooltip: i18n.t('harvestedTonsTooltip'),
      format: formatWithCommas,
      total: sum,
      group: 'operational'
    }),
    new DataTableColumn({
      id: 6,
      text: i18n.t('harvested'),
      textAbbr: i18n.t('harvestedAbbr'),
      value: 'percentageOfTotalHarvest',
      align: 'right',
      tooltip: i18n.t('harvestedPercentage'),
      format: formatPct,
      total: sum,
      group: 'operational',
      columnAdjusterText: i18n.t('harvestedPercentage')
    }),
    new DataTableColumn({
      id: 7,
      text: isTons ? i18n.t('remainingCruiseTons') : i18n.t('remainingCruiseMBF'),
      textAbbr: isTons ? i18n.t('remainingCruiseTonsAbbr') : i18n.t('remainingCruiseMBFAbbr'),
      value: isTons ? 'remainingTons' : 'remainingMBF',
      align: 'right',
      tooltip: i18n.t('remainingTonsTooltip'),
      format: formatWithCommas,
      total: sum,
      group: 'operational'
    }),
    new DataTableColumn({
      id: 8,
      text: i18n.t('cutout'),
      value: 'cutoutPercentage',
      align: 'right',
      tooltip: i18n.t('cutoutPercentageTooltip'),
      format: formatPct,
      group: 'operational',
      columnAdjusterText: i18n.t('cutoutPercentage')
    }),
    new DataTableColumn({
      id: 9,
      text: isTons ? i18n.t('overcutTons') : i18n.t('overcutMBF'),
      value: isTons ? 'overcutTons' : 'overcutMBF',
      align: 'right',
      tooltip: i18n.t('overcutTonsTooltip'),
      format: formatWithCommas,
      total: sum,
      group: 'operational'
    }),
    new DataTableColumn({
      id: 10,
      text: i18n.t('overcut'),
      value: 'overcutPercentage',
      align: 'right',
      tooltip: i18n.t('overcutPercentageTooltip'),
      format: formatPct,
      group: 'operational',
      columnAdjusterText: i18n.t('overcutPercentage')
    }),
    new DataTableColumn({
      id: 18,
      text: i18n.t('harvestedCruise'),
      textAbbr: i18n.t('harvestedCruiseAbbr'),
      value: 'harvestedCruiseValue',
      align: 'right',
      tooltip: i18n.t('harvestedCruiseValueTooltip'),
      format: formatMoney,
      total: sum,
      group: 'financial',
      columnAdjusterText: i18n.t('harvestedCruiseValue')
    }),
    new DataTableColumn({
      id: 19,
      text: i18n.t('overUnderCruise'),
      value: 'overUnderCruiseValue',
      align: 'right',
      tooltip: i18n.t('overUnderCruiseValueTooltip'),
      format: formatMoney,
      total: sum,
      group: 'financial'
    }),
    new DataTableColumn({
      id: 11,
      text: i18n.t('remainingCruise'),
      textAbbr: i18n.t('remainingCruiseValueAbbr'),
      value: 'remainingCruiseValue',
      align: 'right',
      tooltip: i18n.t('remainingCruiseValueTooltip'),
      format: formatMoney,
      total: sum,
      group: 'financial',
      columnAdjusterText: i18n.t('remainingCruiseValue')
    }),
    // Start depletion-status-dependent section
    new DataTableColumn({
      id: 25,
      text: i18n.t('depletion'),
      value: 'depletion',
      align: 'right',
      tooltip: i18n.t('depletionCostsToDate'),
      format: formatMoney,
      total: sum,
      group: 'financial',
      columnAdjusterText: i18n.t('depletion')
    }),
    !isUnit ? new DataTableColumn({
      id: 20,
      text: i18n.t('overUnderPurchase'),
      textAbbr: i18n.t('overUnderPurchaseAbbr'),
      value: 'overUnderPurchaseValue',
      align: 'right',
      tooltip: i18n.t('overUnderPurchaseValueTooltip'),
      format: formatMoney,
      total: sum,
      group: 'financial',
      columnAdjusterText: i18n.t('overUnderPurchaseValue')
    }) : undefined,
    !isUnit ? new DataTableColumn({
      id: 26,
      text: i18n.t('costBalance'),
      value: 'remainingPurchaseValue',
      align: 'right',
      tooltip: i18n.t('remainingCostValueOfTract'),
      format: formatMoney,
      group: 'financial',
      columnAdjusterText: i18n.t('costBalance')
    }) : undefined,
    new DataTableColumn({
      id: 27,
      text: i18n.t('depletionBalance'),
      value: 'balance',
      align: 'right',
      tooltip: i18n.t('totalCostMinusDepletion'),
      format: formatMoney,
      total: sum,
      group: 'financial',
      columnAdjusterText: i18n.t('depletionBalance')
    }),
    new DataTableColumn({
      id: 15,
      text: i18n.t('stumpage'),
      value: 'totalStumpageCost',
      align: 'right',
      tooltip: i18n.t('stumpageCostCruiseTooltip'),
      format: formatMoney,
      total: sum,
      group: 'financial',
      columnAdjusterText: i18n.t('stumpageCostCruise')
    }),
    // End depletion-status-dependent section
    new DataTableColumn({
      id: 14,
      text: i18n.t('production'),
      textAbbr: i18n.t('productionAbbr'),
      value: 'totalProductionCost',
      align: 'right',
      tooltip: i18n.t('totalProductionCostCruiseTooltip'),
      format: formatMoney,
      total: sum,
      group: 'financial',
      columnAdjusterText: i18n.t('totalProductionCostCruise')
    }),
    new DataTableColumn({
      id: 16,
      text: i18n.t('total'),
      value: 'totalCost',
      align: 'right',
      tooltip: i18n.t('totalCruiseCostTooltip'),
      hidable: false,
      format: formatMoney,
      total: sum
    }),
    isStumpage ? new DataTableColumn({
      id: 22,
      text: i18n.t('revenue'),
      value: 'revenue',
      align: 'right',
      tooltip: i18n.t('revenueCruiseTooltip'),
      format: formatMoney,
      total: sum,
      group: 'financial'
    }) : undefined,
    isStumpage ? new DataTableColumn({
      id: 23,
      text: i18n.t('grossProfit'),
      value: 'grossProfit',
      align: 'right',
      tooltip: i18n.t('grossProfitCruiseTooltip'),
      format: formatMoney,
      total: sum,
      group: 'financial'
    }) : undefined,
    isStumpage ? new DataTableColumn({
      id: 24,
      text: isTons ? i18n.t('grossProfitPerTon') : i18n.t('grossProfitPerMBF'),
      value: isTons ? 'grossProfitPerTon' : 'grossProfitPerMBF',
      align: 'right',
      tooltip: isTons ? i18n.t('grossProfitPerTonCruiseTooltip') : i18n.t('grossProfitPerMBFCruiseTooltip'),
      format: formatMoney,
      group: 'financial',
      total: ({ sums }) => sums.grossProfit / (isTons ? sums.harvestedTons : sums.harvestedMBF)
    }) : undefined
  ].filter(dt => dt !== undefined)

  return new ColumnSet({
    columns: columns,
    setName: 'TractDepletionHarvestHeaders',
    userSettingKey: SETTING_KEYS.TRACT_CRUISE_DEPLETION_HIDDEN_COLUMNS
  })
}

export const tractPtDownloadHeaders = () => ([
  {
    sortable: true,
    text: i18n.t('name'),
    value: 'name'
  },
  {
    sortable: true,
    text: i18n.t('code'),
    value: 'code'
  },
  {
    sortable: true,
    text: i18n.t('type'),
    value: 'type'
  },
  {
    sortable: false,
    text: i18n.t('actions'),
    value: 'actions',
    align: 'center'
  }
])

export const tractBulkImportPreviewHeaders = () => {
  return ([
    {
      sortable: true,
      text: i18n.t('name'),
      value: 'name'
    },
    {
      sortable: true,
      text: i18n.t('code'),
      value: 'code'
    },
    {
      sortable: true,
      text: i18n.t('typeName'),
      value: 'typeName'
    },
    {
      sortable: true,
      text: i18n.t('logger'),
      value: 'logger'
    },
    {
      sortable: true,
      text: i18n.t('landOwner'),
      value: 'landOwner'
    },
    {
      sortable: true,
      text: i18n.t('businessEntity'),
      value: 'businessEntity'
    },
    {
      sortable: true,
      text: i18n.t('cost'),
      value: 'cost',
      align: 'right',
      format: formatMoney
    },
    {
      sortable: true,
      text: i18n.t('acres'),
      value: 'acres',
      align: 'right'
    },
    {
      sortable: true,
      text: i18n.t('mapAndParcelNumber'),
      value: 'mapAndParcelNumber'
    },
    {
      sortable: false,
      text: i18n.t('coordinates'),
      value: 'coordinates'
    },
    {
      sortable: true,
      text: i18n.t('location'),
      value: 'location'
    }
  ])
}

export const tractGroupAnalysisHeaders = (isSpecies, isUncapped) => {
  const columns = [
    new DataTableColumn({
      id: 0,
      text: isSpecies ? i18n.t('species') : i18n.t('product'),
      value: isSpecies ? 'speciesName' : 'productName',
      tooltip: isSpecies ? i18n.t('speciesTooltip') : i18n.t('productTooltip'),
      hidable: false
    }),
    new DataTableColumn({
      id: 1,
      text: isSpecies ? i18n.t('cruisePricePerMBF') : i18n.t('cruisePricePerTon'),
      value: 'cruisedRate',
      align: 'right',
      group: 'cruise',
      tooltip: isSpecies ? i18n.t('cruisePricePerMBFTooltip') : i18n.t('cruisePricePerTonTooltip'),
      format: formatMoney
    }),
    new DataTableColumn({
      id: 2,
      text: isSpecies ? i18n.t('cruisedMBF') : i18n.t('cruisedTons'),
      value: 'cruisedVolume',
      align: 'right',
      group: 'cruise',
      tooltip: isSpecies ? i18n.t('cruisedMBFTooltip') : i18n.t('cruisedTonsTooltip'),
      format: formatWithCommas,
      total: sum
    }),
    new DataTableColumn({
      id: 3,
      text: isSpecies ? i18n.t('harvestedMBF') : i18n.t('harvestedTons'),
      value: 'harvestedVolume',
      align: 'right',
      group: 'operational',
      tooltip: isSpecies ? i18n.t('harvestedMBFTooltip') : i18n.t('harvestedTonsTooltip'),
      format: formatWithCommas,
      total: sum
    }),
    new DataTableColumn({
      id: 15,
      text: i18n.t('harvested'),
      textAbbr: i18n.t('harvestedAbbr'),
      value: 'harvestedPercentage',
      align: 'right',
      group: 'operational',
      tooltip: isSpecies ? i18n.t('harvestedMBFPercentageTooltip') : i18n.t('harvestedTonsPercentageTooltip'),
      columnAdjusterText: isSpecies ? i18n.t('harvestedPercentage') : i18n.t('harvestedPercentage'),
      format: formatPct
    }),
    new DataTableColumn({
      id: 4,
      text: i18n.t('cutout'),
      value: 'cutoutPercentage',
      align: 'right',
      group: 'operational',
      tooltip: i18n.t('cutoutPercentageTooltip'),
      columnAdjusterText: i18n.t('cutoutPercentage'),
      format: formatPct
    }),
    !isSpecies ? new DataTableColumn({
      id: 16,
      text: i18n.t('loads'),
      value: 'loadCount',
      align: 'right',
      group: 'operational',
      tooltip: i18n.t('loadsTooltip'),
      total: sum
    }) : undefined,
    isSpecies && isUncapped ? new DataTableColumn({
      id: 5,
      text: i18n.t('obligationPerMBF'),
      value: 'obligationRate',
      align: 'right',
      group: 'financial',
      tooltip: i18n.t('obligationPerMBFTooltip'),
      format: formatMoney
    }) : undefined,
    isSpecies && isUncapped ? new DataTableColumn({
      id: 6,
      text: i18n.t('totalObligation'),
      value: 'totalObligation',
      align: 'right',
      group: 'financial',
      tooltip: i18n.t('totalObligationTooltip'),
      format: formatMoney,
      total: sum
    }) : undefined,
    isSpecies ? new DataTableColumn({
      id: 17,
      text: i18n.t('adjustedObligationAccrued'),
      value: 'adjustedObligationAccrued',
      align: 'right',
      group: 'financial',
      tooltip: i18n.t('adjustedObligationAccruedTooltip'),
      format: formatMoney,
      total: sum
    }) : undefined,
    isSpecies && isUncapped ? new DataTableColumn({
      id: 9,
      text: i18n.t('obligationBalance'),
      value: 'obligationBalance',
      align: 'right',
      group: 'financial',
      tooltip: i18n.t('obligationBalanceTooltip'),
      format: formatMoney,
      total: sum
    }) : undefined,
    new DataTableColumn({
      id: 12,
      text: i18n.t('stumpage'),
      value: 'stumpageAmount',
      align: 'right',
      group: 'financial',
      tooltip: i18n.t('stumpageCostCruiseTooltip'),
      columnAdjusterText: i18n.t('stumpageCostCruise'),
      format: formatMoney,
      total: sum
    }),
    new DataTableColumn({
      id: 13,
      text: i18n.t('production'),
      value: 'productionAmount',
      align: 'right',
      group: 'financial',
      tooltip: i18n.t('totalProductionCostCruiseTooltip'),
      columnAdjusterText: i18n.t('totalProductionCostCruise'),
      format: formatMoney,
      total: sum
    }),
    new DataTableColumn({
      id: 14,
      text: i18n.t('total'),
      value: 'totalCost',
      align: 'right',
      hidable: false,
      tooltip: i18n.t('totalCruiseCostTooltip'),
      format: formatMoney,
      total: sum
    })
  ].filter(dt => dt !== undefined)

  return new ColumnSet({
    columns: columns,
    setName: isSpecies ? 'TractGroupAnalysisSpeciesHeaders' : 'TractGroupAnalysisProductHeaders',
    userSettingKey: isSpecies ? SETTING_KEYS.TRACT_GROUP_ANALYSIS_HIDDEN_COLUMNS : SETTING_KEYS.TRACT_GROUP_ANALYSIS_PRODUCT_HIDDEN_COLUMNS
  })
}
